import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import React from "react";
import Svg, { G, Path, Rect, Circle, Defs, ClipPath } from "react-native-svg";
import { styleConstants as C } from "~/style/styleConstants";
import type { TextStyle } from "react-native";
import type { SvgProps } from "react-native-svg";

export interface IconProps {
  name:
    | "about-app"
    | "account-arrow-right"
    | "account-heart-outline"
    | "apple"
    | "arrow-back"
    | "arrow-left"
    | "arrow-right"
    | "bell-alert-outline"
    | "bell-check-outline"
    | "bell-disabled"
    | "bell-large"
    | "bell"
    | "bio"
    | "blocked-user"
    | "bluetooth"
    | "calendar"
    | "camera-flip"
    | "camera-image"
    | "camera-large"
    | "camera-plus"
    | "check-circle-outline"
    | "checkbox-blank-outline"
    | "checkbox-marked-outline"
    | "checkbox-marked"
    | "checkmark"
    | "chevron-double-down"
    | "chevron-down"
    | "chevron-left"
    | "chevron-right"
    | "chevron-up"
    | "clock"
    | "close"
    | "compass"
    | "confused-face"
    | "confused-face"
    | "cursor-text"
    | "date-of-birth"
    | "delete-account"
    | "delete"
    | "dislike"
    | "dot"
    | "edit"
    | "exclamation-mark-large"
    | "exclamation-mark"
    | "eye-closed"
    | "eye"
    | "face-man"
    | "face-woman"
    | "feedback"
    | "file-image-plus"
    | "file-upload-outline"
    | "filter-outline"
    | "flag"
    | "flash-outline"
    | "flash"
    | "forward"
    | "fullscreen-exit"
    | "fullscreen"
    | "gallery-video"
    | "gallery"
    | "gender"
    | "google"
    | "hamburger-menu"
    | "headphones"
    | "heart-broken-outline"
    | "heart-eyes-face"
    | "heart-full"
    | "heart"
    | "human-non-binary"
    | "information-outline"
    | "interested-in"
    | "language"
    | "legal"
    | "light-bulb"
    | "like"
    | "location-large"
    | "location-pin"
    | "lock"
    | "logout"
    | "mic-off"
    | "mic-on"
    | "microphone-question-outline"
    | "multiple-hearts"
    | "name"
    | "neutral-face"
    | "notification"
    | "pause"
    | "people"
    | "photo-placeholder"
    | "play-alt"
    | "play-video"
    | "play"
    | "playlist-check"
    | "profile-picture"
    | "radio-button-off"
    | "radio-button-on"
    | "refresh"
    | "report"
    | "sad-face"
    | "security"
    | "send"
    | "settings"
    | "share-variant-outline"
    | "shield-check"
    | "shuffle"
    | "sliders"
    | "sparkles"
    | "speaker"
    | "splash"
    | "stop"
    | "success"
    | "three-dot-menu"
    | "thumbs-up-down-outline"
    | "update"
    | "upload"
    | "user"
    | "video-date"
    | "video-off"
    | "video-on"
    | "video-placeholder"
    | "video"
    | "volume-high"
    | "volume-off"
    | "warning"
    | "wrench-outline"
    | "account-cog-outline";

  color?: string;
  size?: number;
  style?: TextStyle & SvgProps["style"];
}

export function Icon({
  name,
  color = C.colorTextLight,
  size = 28,
  ...props
}: IconProps) {
  switch (name) {
    case "forward":
      return (
        <Svg width={size} height={size} fill={color} viewBox="0 0 24 24">
          <Path d="M12,8V4L20,12L12,20V16H4V8H12Z" />
        </Svg>
      );
    case "stop":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <Path d="M18,18H6V6H18V18Z" />
        </Svg>
      );
    case "dot":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 15 15"
          fill="none"
          {...props}
        >
          <Circle cx={7.5} cy={7.5} r={7.5} fill={color} />
        </Svg>
      );
    case "clock":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 17 17"
          fill="none"
          {...props}
        >
          <Path
            d="M8.60078 4.9001V8.4001H12.1008M8.60078 14.7001C5.12139 14.7001 2.30078 11.8795 2.30078 8.4001C2.30078 4.9207 5.12139 2.1001 8.60078 2.1001C12.0802 2.1001 14.9008 4.9207 14.9008 8.4001C14.9008 11.8795 12.0802 14.7001 8.60078 14.7001Z"
            stroke={color}
            stroke-width="1.05"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </Svg>
      );

    case "speaker":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fill={color}
            d="M16.692 21.5H7.308a1.74 1.74 0 0 1-1.277-.531 1.74 1.74 0 0 1-.531-1.277V4.308c0-.497.177-.923.531-1.277A1.74 1.74 0 0 1 7.308 2.5h9.384c.497 0 .923.177 1.277.531.354.354.531.78.531 1.277v15.384c0 .497-.177.923-.531 1.277a1.74 1.74 0 0 1-1.277.531Zm0-1.5a.3.3 0 0 0 .221-.087.3.3 0 0 0 .087-.22V4.307a.3.3 0 0 0-.087-.221.3.3 0 0 0-.22-.087H7.307a.3.3 0 0 0-.221.087.3.3 0 0 0-.087.22v15.385a.3.3 0 0 0 .087.221.3.3 0 0 0 .22.087h9.385Zm-4.69-11.192c.497 0 .923-.178 1.276-.532.353-.354.53-.78.53-1.278 0-.497-.178-.923-.532-1.276a1.745 1.745 0 0 0-1.278-.53c-.497 0-.923.177-1.276.532-.353.354-.53.78-.53 1.278 0 .497.178.923.532 1.276.354.353.78.53 1.278.53ZM12 18.73c1.03 0 1.909-.365 2.637-1.094A3.594 3.594 0 0 0 15.731 15c0-1.03-.365-1.909-1.094-2.637A3.595 3.595 0 0 0 12 11.269c-1.03 0-1.909.365-2.637 1.094A3.594 3.594 0 0 0 8.269 15c0 1.03.365 1.909 1.094 2.637A3.594 3.594 0 0 0 12 18.732Zm0-1.5a2.146 2.146 0 0 1-1.576-.656A2.15 2.15 0 0 1 9.769 15c0-.613.219-1.138.656-1.575A2.15 2.15 0 0 1 12 12.769c.613 0 1.138.219 1.575.656.436.437.655.962.655 1.576 0 .613-.219 1.138-.656 1.575a2.15 2.15 0 0 1-1.576.655Z"
          />
        </Svg>
      );
    case "flag":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fill={color}
            d="M5.5 20.75V4.5h8.096l.4 2H19.5v9h-6.096l-.4-2H7v7.25H5.5ZM14.65 14H18V8h-5.25l-.4-2H7v6h7.25l.4 2Z"
          />
        </Svg>
      );
    case "headphones":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fill={color}
            d="M8.077 20H5.615c-.46 0-.844-.154-1.152-.462A1.565 1.565 0 0 1 4 18.385V12c0-1.109.209-2.148.626-3.118a8.066 8.066 0 0 1 1.713-2.543 8.066 8.066 0 0 1 2.543-1.713A7.805 7.805 0 0 1 12 4c1.109 0 2.148.209 3.118.626.97.417 1.818.988 2.543 1.713a8.065 8.065 0 0 1 1.713 2.543c.417.97.626 2.009.626 3.118v6.385c0 .46-.154.844-.462 1.152a1.565 1.565 0 0 1-1.153.463h-2.462v-6.154H19V12c0-1.95-.68-3.604-2.038-4.963C15.604 5.68 13.95 5 12 5c-1.95 0-3.604.68-4.963 2.037C5.68 8.396 5 10.05 5 12v1.846h3.077V20Zm-1-5.154H5v3.539c0 .153.064.294.192.423.129.128.27.192.423.192h1.462v-4.154Zm9.846 0V19h1.462a.588.588 0 0 0 .423-.192.588.588 0 0 0 .192-.423v-3.539h-2.077Z"
          />
        </Svg>
      );

    case "bluetooth":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fill={color}
            d="M11.25 21.654v-7.85l-4.85 4.85L5.346 17.6l5.6-5.6-5.6-5.6L6.4 5.346l4.85 4.85v-7.85H12L17.352 7.7l-4.3 4.3 4.3 4.3L12 21.654h-.75Zm1.5-11.458L15.245 7.7l-2.497-2.465v4.961Zm0 8.57 2.496-2.466-2.497-2.496v4.961Z"
          />
        </Svg>
      );
    case "chevron-up":
      return (
        <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.2929 10.7071C12.6834 11.0976 13.3166 11.0976 13.7071 10.7071C14.0976 10.3166 14.0976 9.68342 13.7071 9.29289L8.70711 4.29289C8.31658 3.90237 7.68342 3.90237 7.29289 4.29289L2.29289 9.29289C1.90237 9.68342 1.90237 10.3166 2.29289 10.7071C2.68342 11.0976 3.31658 11.0976 3.70711 10.7071L8 6.41421L12.2929 10.7071Z"
            fill={color}
          />
        </Svg>
      );
    case "chevron-down":
      return (
        <Svg width={size} height={size} viewBox="0 0 16 16" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12.2929 5.29289C12.6834 4.90237 13.3166 4.90237 13.7071 5.29289C14.0976 5.68342 14.0976 6.31658 13.7071 6.70711L8.70711 11.7071C8.31658 12.0976 7.68342 12.0976 7.29289 11.7071L2.29289 6.70711C1.90237 6.31658 1.90237 5.68342 2.29289 5.29289C2.68342 4.90237 3.31658 4.90237 3.70711 5.29289L8 9.58579L12.2929 5.29289Z"
            fill={color}
          />
        </Svg>
      );
    case "video-date":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <G clipPath="url(#clip0_1501_14220)">
            <Path
              d="M5.50402 16.3846H14.2732V15.9554C14.2732 15.1984 13.8659 14.5807 13.0513 14.1023C12.2367 13.6238 11.1825 13.3846 9.88861 13.3846C8.59477 13.3846 7.54055 13.6238 6.72595 14.1023C5.91133 14.5807 5.50402 15.1984 5.50402 15.9554V16.3846ZM9.88861 11.8154C10.4717 11.8154 10.9675 11.6111 11.3759 11.2027C11.7844 10.7942 11.9886 10.2984 11.9886 9.71535C11.9886 9.13229 11.7844 8.63653 11.3759 8.22807C10.9675 7.81961 10.4717 7.61538 9.88861 7.61538C9.30555 7.61538 8.80979 7.81961 8.40133 8.22807C7.99287 8.63653 7.78864 9.13229 7.78864 9.71535C7.78864 10.2984 7.99287 10.7942 8.40133 11.2027C8.80979 11.6111 9.30555 11.8154 9.88861 11.8154ZM3.05788 20.9999C2.45172 20.9999 1.93864 20.7899 1.51864 20.3699C1.09866 19.9499 0.888672 19.4369 0.888672 18.8307V5.16924C0.888672 4.56308 1.09866 4.05 1.51864 3.63C1.93864 3.21 2.45172 3 3.05788 3H16.7193C17.3255 3 17.8386 3.21 18.2586 3.63C18.6786 4.05 18.8886 4.56308 18.8886 5.16924V10.6616L23.1116 6.43851V17.5614L18.8886 13.3384V18.8307C18.8886 19.4369 18.6786 19.9499 18.2586 20.3699C17.8386 20.7899 17.3255 20.9999 16.7193 20.9999H3.05788ZM3.05788 19.2H16.7193C16.8271 19.2 16.9155 19.1653 16.9848 19.0961C17.054 19.0269 17.0886 18.9384 17.0886 18.8307V5.16924C17.0886 5.06154 17.054 4.97307 16.9848 4.90383C16.9155 4.83459 16.8271 4.79997 16.7193 4.79997H3.05788C2.95018 4.79997 2.86171 4.83459 2.79247 4.90383C2.72323 4.97307 2.68861 5.06154 2.68861 5.16924V18.8307C2.68861 18.9384 2.72323 19.0269 2.79247 19.0961C2.86171 19.1653 2.95018 19.2 3.05788 19.2Z"
              fill={color}
            />
          </G>
          <Defs>
            <ClipPath id="clip0_1501_14220">
              <Rect width={size} height={size} fill="white" />
            </ClipPath>
          </Defs>
        </Svg>
      );
    case "mic-on":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M19 11C19.5523 11 20 11.4477 20 12C20 16.0793 16.9468 19.4455 13.001 19.938L13 21C13 21.5523 12.5523 22 12 22C11.4477 22 11 21.5523 11 21L11 19.9381C7.05371 19.446 4 16.0796 4 12C4 11.4477 4.44772 11 5 11C5.55228 11 6 11.4477 6 12C6 15.3137 8.68629 18 12 18C15.3137 18 18 15.3137 18 12C18 11.4477 18.4477 11 19 11ZM12 2C14.2091 2 16 3.79086 16 6V12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12V6C8 3.79086 9.79086 2 12 2ZM12 4C10.8954 4 10 4.89543 10 6V12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12V6C14 4.89543 13.1046 4 12 4Z"
            fill={color}
          />
        </Svg>
      );
    case "mic-off":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M5.70423 5.70414L19.0712 19.0711C19.4617 19.4616 19.4617 20.0948 19.0712 20.4853C18.6806 20.8758 18.0475 20.8758 17.6569 20.4853L16.0639 18.8924C15.1451 19.4354 14.1082 19.7998 13.001 19.938L13.0001 21C13.0001 21.5523 12.5524 22 12.0001 22C11.4478 22 11.0001 21.5523 11.0001 21L11.0001 19.9381C7.05381 19.446 4.00009 16.0796 4.00009 12C4.00009 11.4477 4.44781 11 5.00009 11C5.55238 11 6.00009 11.4477 6.00009 12C6.00009 15.3137 8.68638 18 12.0001 18C12.926 18 13.803 17.7903 14.586 17.4157L13.035 15.8648C12.7049 15.953 12.358 16 12.0001 16C9.79095 16 8.00009 14.2091 8.00009 12L8.00002 10.829L4.29192 7.12025C3.90139 6.72973 3.90139 6.09656 4.29192 5.70604C4.68181 5.31615 5.31356 5.31552 5.70423 5.70414ZM19.0001 11C19.5524 11 20.0001 11.4477 20.0001 12C20.0001 13.4835 19.5963 14.8727 18.8926 16.0637L17.4156 14.5863C17.7903 13.8032 18.0001 12.9261 18.0001 12C18.0001 11.4477 18.4478 11 19.0001 11ZM12.0001 2C14.2092 2 16.0001 3.79086 16.0001 6V12C16.0001 12.3579 15.9531 12.7049 15.8649 13.035L14 11.171L14.0001 6C14.0001 4.89543 13.1047 4 12.0001 4C10.8955 4 10.0001 4.89543 10.0001 6L10 7.171L8.07154 5.24342C8.4252 3.39604 10.0496 2 12.0001 2Z"
            fill={color}
          />
        </Svg>
      );
    case "shuffle":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <G id="Media / Shuffle">
            <Path
              id="Vector"
              d="M18 20L21 17M21 17L18 14M21 17H17C14.2386 17 12 14.7614 12 12C12 9.23858 9.76142 7 7 7H3M18 4L21 7M21 7L18 10M21 7L17 7C15.8744 7 14.8357 7.37194 14 7.99963M3 17H7C8.12561 17 9.16434 16.6277 10 16"
              stroke={color}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </G>
        </Svg>
      );
    case "video-on":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            d="M21.2884 16.6345L17.7692 13.1153L17.75 17.8269L16.2693 13.5116V6.3077C16.2693 6.21795 16.2404 6.14422 16.1827 6.08652C16.125 6.02882 16.0513 5.99998 15.9615 5.99998H8.75769L4.44236 4.51925L15.9615 4.5C16.4667 4.5 16.8942 4.675 17.2442 5.025C17.5942 5.375 17.7692 5.80257 17.7692 6.3077V10.8846L21.2884 7.36543V16.6345ZM4.44236 4.51925L6 5L8.75769 5.99998H4.57699C4.48724 5.99998 4.41351 6.02882 4.35581 6.08652C4.29811 6.14422 4.26926 6.21795 4.26926 6.3077V17.6923C4.26926 17.782 4.29811 17.8557 4.35581 17.9134C4.41351 17.9711 4.48724 18 4.57699 18H15.9615C16.0513 18 16.125 17.9711 16.1827 17.9134C16.2404 17.8557 16.2693 17.782 16.2693 17.6923V13.5116L17.75 17.8269C17.7179 18.2935 17.5317 18.6891 17.1913 19.0134C16.851 19.3378 16.441 19.4999 15.9615 19.4999H4.57699C4.07185 19.4999 3.64429 19.3249 3.29429 18.9749C2.94429 18.6249 2.76929 18.1974 2.76929 17.6923V6.3077C2.76929 5.82822 2.93147 5.41828 3.25584 5.0779C3.58019 4.73752 3.9757 4.5513 4.44236 4.51925Z"
            fill={color}
          />
        </Svg>
      );
    case "video-off":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          {...props}
        >
          <Path
            d="M21.2884 16.6345L17.7692 13.1153V15.0115L16.2692 13.5116V6.30771C16.2692 6.21796 16.2404 6.14423 16.1827 6.08653C16.125 6.02883 16.0513 5.99998 15.9615 5.99998H8.75767L7.25772 4.5H15.9615C16.4667 4.5 16.8942 4.675 17.2442 5.025C17.5942 5.375 17.7692 5.80257 17.7692 6.30771V10.8846L21.2884 7.36543V16.6345ZM19.973 22.1576L1.53467 3.71923L2.58849 2.66541L21.0268 21.1038L19.973 22.1576ZM4.44234 4.51926L5.92307 5.99998H4.57697C4.48722 5.99998 4.41349 6.02883 4.35579 6.08653C4.29809 6.14423 4.26924 6.21796 4.26924 6.30771V17.6923C4.26924 17.782 4.29809 17.8557 4.35579 17.9134C4.41349 17.9711 4.48722 18 4.57697 18H15.9615C16.0513 18 16.125 17.9711 16.1827 17.9134C16.2404 17.8557 16.2692 17.782 16.2692 17.6923V16.3462L17.75 17.8269C17.7179 18.2935 17.5317 18.6891 17.1913 19.0134C16.8509 19.3378 16.441 19.5 15.9615 19.5H4.57697C4.07183 19.5 3.64427 19.325 3.29427 18.975C2.94427 18.625 2.76927 18.1974 2.76927 17.6923V6.30771C2.76927 5.82822 2.93145 5.41829 3.25582 5.07791C3.58017 4.73752 3.97568 4.55131 4.44234 4.51926Z"
            fill={color}
          />
        </Svg>
      );
    case "flash-outline":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M7,2H17L13.5,9H17L10,22V14H7V2M9,4V12H12V14.66L14,11H10.24L13.76,4H9Z"
          />
        </Svg>
      );
    case "flash":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path fill={color} d="M7,2V13H10V22L17,10H13L17,2H7Z" />
        </Svg>
      );
    case "camera-flip":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M20 5H16.83L15 3H9L7.17 5H4C2.9 5 2 5.9 2 7V19C2 20.11 2.9 21 4 21H20C21.11 21 22 20.11 22 19V7C22 5.9 21.11 5 20 5M20 19H4V7H8.05L9.88 5H14.12L16 7H20V19M5 12H7.1C7.65 9.29 10.29 7.55 13 8.1C13.76 8.25 14.43 8.59 15 9L13.56 10.45C13.11 10.17 12.58 10 12 10C10.74 10 9.6 10.8 9.18 12H11L8 15L5 12M16.91 14C16.36 16.71 13.72 18.45 11 17.9C10.25 17.74 9.58 17.41 9 17L10.44 15.55C10.9 15.83 11.43 16 12 16C13.27 16 14.41 15.2 14.83 14H13L16 11L19 14H16.91Z"
          />
        </Svg>
      );
    case "gallery-video":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <Path
            stroke={color}
            strokeWidth={1.5}
            d="M3.517 7.36h2.586m11.38 0H6.103M3 14.64h6m8.483 0H9M6.103 18v-3.36m0-7.28V4m0 0H3v14h6M6.103 4H18v14H9m0 0v-3.36M9 7V4m3 14v-3m0-8V4m3 13.5V15m0-8V4"
          />
          <Path stroke={color} strokeWidth={1.5} d="M19.5 9h2v12H7v-1.5" />
          <Path
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            d="M10 9.834 11.683 11 10 12.166V9.834Z"
          />
        </Svg>
      );
    case "refresh":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.29289 5.29289L10.2929 3.29289C10.6834 2.90237 11.3166 2.90237 11.7071 3.29289C12.0676 3.65338 12.0953 4.22061 11.7903 4.6129L11.7071 4.70711L11.3892 5.02302C11.5916 5.00771 11.7953 5 12 5C16.4183 5 20 8.58172 20 13C20 17.4183 16.4183 21 12 21C7.58172 21 4 17.4183 4 13C4 12.4477 4.44772 12 5 12C5.55228 12 6 12.4477 6 13C6 16.3137 8.68629 19 12 19C15.3137 19 18 16.3137 18 13C18 9.68629 15.3137 7 12 7C11.8129 7 11.6271 7.00853 11.4429 7.02545L11.7071 7.29289C12.0976 7.68342 12.0976 8.31658 11.7071 8.70711C11.3466 9.06759 10.7794 9.09532 10.3871 8.7903L10.2929 8.70711L8.29289 6.70711C7.93241 6.34662 7.90468 5.77939 8.2097 5.3871L8.29289 5.29289L10.2929 3.29289L8.29289 5.29289Z"
            fill={color}
          />
        </Svg>
      );
    case "checkmark":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <G>
            <Path
              d="M6 12L10.2426 16.2426L18.727 7.75732"
              stroke={color}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </G>
        </Svg>
      );
    case "pause":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" {...props}>
          <Path fill={color} d="M14,19H18V5H14M6,19H10V5H6V19Z" />
        </Svg>
      );
    case "gallery":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 25 24"
          fill="none"
          {...props}
        >
          <Path
            d="M3.83301 16.5H2.33301V3C2.3334 2.6023 2.49157 2.221 2.77279 1.93978C3.054 1.65856 3.4353 1.5004 3.83301 1.5H17.333V3H3.83301V16.5Z"
            fill={color}
          />
          <Path
            d="M16.583 12.75C17.028 12.75 17.463 12.618 17.833 12.3708C18.2031 12.1236 18.4914 11.7722 18.6617 11.361C18.832 10.9499 18.8766 10.4975 18.7898 10.061C18.703 9.62459 18.4887 9.22368 18.174 8.90901C17.8593 8.59434 17.4584 8.38005 17.022 8.29323C16.5855 8.20642 16.1331 8.25097 15.722 8.42127C15.3108 8.59157 14.9594 8.87996 14.7122 9.24997C14.465 9.61998 14.333 10.055 14.333 10.5C14.3336 11.0966 14.5708 11.6685 14.9927 12.0903C15.4145 12.5122 15.9865 12.7494 16.583 12.75ZM16.583 9.75C16.7313 9.75 16.8763 9.79399 16.9997 9.8764C17.123 9.95881 17.2192 10.0759 17.2759 10.213C17.3327 10.35 17.3475 10.5008 17.3186 10.6463C17.2897 10.7918 17.2182 10.9254 17.1133 11.0303C17.0084 11.1352 16.8748 11.2067 16.7293 11.2356C16.5838 11.2645 16.433 11.2497 16.296 11.1929C16.159 11.1361 16.0418 11.04 15.9594 10.9167C15.877 10.7933 15.833 10.6483 15.833 10.5C15.833 10.3011 15.912 10.1103 16.0527 9.96967C16.1933 9.82902 16.3841 9.75 16.583 9.75Z"
            fill={color}
          />
          <Path
            d="M21.833 5.25H7.58301C7.1853 5.2504 6.804 5.40856 6.52279 5.68978C6.24157 5.971 6.0834 6.3523 6.08301 6.75V21C6.0834 21.3977 6.24157 21.779 6.52279 22.0602C6.804 22.3414 7.1853 22.4996 7.58301 22.5H21.833C22.2307 22.4996 22.612 22.3414 22.8932 22.0602C23.1744 21.779 23.3326 21.3977 23.333 21V6.75C23.3326 6.3523 23.1744 5.971 22.8932 5.68978C22.612 5.40856 22.2307 5.2504 21.833 5.25ZM21.833 21H7.58301V16.5L10.583 13.5023L14.7725 17.6918C15.0538 17.973 15.4353 18.1309 15.833 18.1309C16.2308 18.1309 16.6122 17.973 16.8935 17.6918L18.083 16.5023L21.833 20.25V21ZM21.833 18.129L19.1435 15.4395C18.8622 15.1583 18.4808 15.0003 18.083 15.0003C17.6853 15.0003 17.3038 15.1583 17.0225 15.4395L15.833 16.629L11.6435 12.4395C11.3622 12.1583 10.9808 12.0003 10.583 12.0003C10.1853 12.0003 9.8038 12.1583 9.52251 12.4395L7.58301 14.379V6.75H21.833V18.129Z"
            fill={color}
          />
        </Svg>
      );
    case "play-video":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 36 36"
          fill="none"
          {...props}
        >
          <Path
            d="M7.5 26.0005V10.0005C7.5 8.68113 7.5 8.02072 7.77764 7.63036C8.01996 7.28966 8.39345 7.06596 8.80811 7.01248C9.28303 6.95123 9.86551 7.26188 11.0288 7.88231L26.0288 15.8823L26.0343 15.8847C27.3198 16.5704 27.9629 16.9134 28.1739 17.3705C28.3579 17.7693 28.3579 18.2296 28.1739 18.6284C27.9626 19.0862 27.318 19.4306 26.0288 20.1182L11.0288 28.1182C9.86468 28.739 9.2832 29.0485 8.80811 28.9872C8.39345 28.9337 8.01996 28.7101 7.77764 28.3694C7.5 27.979 7.5 27.3198 7.5 26.0005Z"
            stroke={color}
            strokeWidth={2.25}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "sad-face":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 44 44"
          fill="none"
          {...props}
        >
          <Path
            d="M44 22C44 34.1501 34.1501 44 22 44C9.85111 44 0 34.1501 0 22C0 9.85111 9.85111 0 22 0C34.1501 0 44 9.85111 44 22Z"
            fill="#FFCC4D"
          />
          <Path
            d="M21.1593 21.5254C20.9442 21.3506 20.6374 21.3457 20.4138 21.5082C20.3991 21.5192 18.8738 22.6107 15.8891 22.6107C12.9057 22.6107 11.3791 21.5192 11.3669 21.5107C11.1432 21.3432 10.8352 21.3481 10.6201 21.5217C10.4026 21.6952 10.3292 21.9935 10.4417 22.2477C10.515 22.4127 12.275 26.2774 15.8891 26.2774C19.5044 26.2774 21.2632 22.4127 21.3366 22.2477C21.449 21.9947 21.3757 21.6989 21.1593 21.5254ZM33.3815 21.5254C33.1664 21.3506 32.8609 21.3445 32.636 21.5082C32.6213 21.5192 31.0948 22.6107 28.1113 22.6107C25.1291 22.6107 23.6013 21.5192 23.5891 21.5107C23.3654 21.3432 23.0587 21.3481 22.8423 21.5217C22.6248 21.6952 22.5514 21.9935 22.6639 22.2477C22.7372 22.4127 24.4972 26.2774 28.1113 26.2774C31.7267 26.2774 33.4854 22.4127 33.5588 22.2477C33.6712 21.9947 33.5979 21.6989 33.3815 21.5254ZM26.8891 34.2218H17.1113C16.4367 34.2218 15.8891 33.6755 15.8891 32.9996C15.8891 32.3237 16.4367 31.7774 17.1113 31.7774H26.8891C27.565 31.7774 28.1113 32.3237 28.1113 32.9996C28.1113 33.6755 27.565 34.2218 26.8891 34.2218ZM7.33355 17.1107C6.65888 17.1107 6.11133 16.5631 6.11133 15.8885C6.11133 15.215 6.65522 14.6687 7.32866 14.6662C7.51933 14.6638 11.6908 14.5611 14.9126 10.2662C15.3171 9.72847 16.0822 9.61602 16.6237 10.0218C17.1639 10.4264 17.2727 11.1927 16.8681 11.7329C12.88 17.0496 7.55844 17.1107 7.33355 17.1107ZM36.6669 17.1107C36.442 17.1107 31.1217 17.0496 27.1336 11.7329C26.7278 11.1927 26.8378 10.4264 27.378 10.0218C27.917 9.6148 28.6821 9.72602 29.0879 10.2662C32.3158 14.5697 36.4958 14.665 36.673 14.6662C37.3452 14.6736 37.8879 15.2236 37.8842 15.8946C37.8818 16.568 37.3391 17.1107 36.6669 17.1107Z"
            fill="#664500"
          />
        </Svg>
      );
    case "confused-face":
      return (
        <Svg x="0px" y="0px" width={size} height={size} viewBox="0,0,256,256">
          <G transform="translate(2.56,2.56) scale(0.98,0.98)">
            <G transform="scale(5.12,5.12)">
              <Path
                fill={color}
                d="M25,2c-0.05176,0.00121 -0.10335,0.00643 -0.1543,0.01563c-12.61863,0.08534 -22.8457,10.34585 -22.8457,22.98438c0,12.69137 10.30863,23 23,23c12.69137,0 23,-10.30863 23,-23c0,-12.6372 -10.22499,-22.89691 -22.8418,-22.98437c-0.05224,-0.0094 -0.10514,-0.01462 -0.1582,-0.01562zM25,4c11.61063,0 21,9.38937 21,21c0,11.61063 -9.38937,21 -21,21c-11.61063,0 -21,-9.38937 -21,-21c0,-11.61063 9.38937,-21 21,-21zM20.66406,8.99805c-0.75887,-0.00853 -2.48836,0.05493 -4.18555,0.69922c0,0.00065 0,0.0013 0,0.00195c-4.237,1.61092 -6.3125,4.75 -6.3125,4.75c-0.21259,0.29786 -0.24552,0.68804 -0.08587,1.01732c0.15965,0.32928 0.4864,0.54507 0.85192,0.56262c0.36552,0.01756 0.71144,-0.16592 0.90192,-0.47838c0,0 1.71051,-2.59734 5.35352,-3.98242c1.74108,-0.66095 3.75,-0.56055 3.75,-0.56055c0.35959,0.02759 0.70618,-0.14057 0.90707,-0.44008c0.20089,-0.29951 0.22497,-0.68398 0.06301,-1.00622c-0.16196,-0.32223 -0.48486,-0.53231 -0.84508,-0.5498c0,0 -0.14548,-0.01083 -0.39844,-0.01367zM28.93359,11.99414c-0.46304,0.02305 -0.84953,0.36132 -0.93373,0.81722c-0.0842,0.4559 0.15596,0.90991 0.58022,1.09684c0,0 3.38167,1.55248 10.20313,3.06836c0.53929,0.11962 1.07346,-0.22054 1.19318,-0.75981c0.11972,-0.53927 -0.22034,-1.0735 -0.75958,-1.19332c-6.67854,-1.48412 -9.79687,-2.93164 -9.79687,-2.93164c-0.12104,-0.05819 -0.25256,-0.0914 -0.38672,-0.09766c-0.03318,-0.00165 -0.06643,-0.00165 -0.09961,0zM17,19c-1.657,0 -3,1.343 -3,3c0,1.657 1.343,3 3,3c1.23,0 2.287,-0.74078 2.75,-1.80078c0.161,-0.367 0.25,-0.77222 0.25,-1.19922c0,-0.527 -0.136,-1.02212 -0.375,-1.45312c-0.512,-0.922 -1.496,-1.54687 -2.625,-1.54687zM33,19c-1.657,0 -3,1.343 -3,3c0,1.657 1.343,3 3,3c1.353,0 2.49609,-0.89595 2.87109,-2.12695c0.084,-0.276 0.12891,-0.56905 0.12891,-0.87305c0,-0.349 -0.05992,-0.68409 -0.16992,-0.99609c-0.411,-1.167 -1.52308,-2.00391 -2.83008,-2.00391zM30,34c-2.25,0 -3.70792,0.85724 -5.39453,1.58008c-1.68661,0.72283 -3.73047,1.41992 -7.60547,1.41992c-0.36064,-0.0051 -0.69608,0.18438 -0.87789,0.49587c-0.18181,0.3115 -0.18181,0.69676 0,1.00825c0.18181,0.3115 0.51725,0.50097 0.87789,0.49587c4.125,0 6.58114,-0.80291 8.39453,-1.58008c1.81339,-0.77717 2.85547,-1.41992 4.60547,-1.41992c2.23333,0 3.44531,0.83203 3.44531,0.83203c0.29723,0.20495 0.68184,0.23379 1.00631,0.07547c0.32447,-0.15833 0.53841,-0.47925 0.55977,-0.83965c0.02135,-0.36041 -0.1532,-0.70434 -0.4567,-0.89988c0,0 -1.78802,-1.16797 -4.55469,-1.16797z"
              />
            </G>
          </G>
        </Svg>
      );
    case "heart-eyes-face":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 44 44"
          fill="none"
          {...props}
        >
          <Path
            d="M44 22C44 34.1501 34.1501 44 22 44C9.84989 44 0 34.1501 0 22C0 9.84989 9.84989 0 22 0C34.1501 0 44 9.84989 44 22Z"
            fill="#FFCC4D"
          />
          <Path
            d="M8.55545 28.1109C11.9305 28.1109 14.6666 25.3749 14.6666 21.9998C14.6666 18.6247 11.9305 15.8887 8.55545 15.8887C5.18037 15.8887 2.44434 18.6247 2.44434 21.9998C2.44434 25.3749 5.18037 28.1109 8.55545 28.1109Z"
            fill="#FF7892"
          />
          <Path
            d="M35.4446 28.1109C38.8197 28.1109 41.5557 25.3749 41.5557 21.9998C41.5557 18.6247 38.8197 15.8887 35.4446 15.8887C32.0695 15.8887 29.3335 18.6247 29.3335 21.9998C29.3335 25.3749 32.0695 28.1109 35.4446 28.1109Z"
            fill="#FF7892"
          />
          <Path
            d="M33.4094 26.4353C33.1918 26.2386 32.8667 26.2263 32.6333 26.3999C32.5856 26.4353 27.8397 29.9443 21.9999 29.9443C16.1748 29.9443 11.413 26.4353 11.3666 26.3999C11.1331 26.2263 10.808 26.241 10.5905 26.4353C10.3741 26.6309 10.3253 26.9536 10.4756 27.2029C10.6333 27.4657 14.4075 33.611 21.9999 33.611C29.5924 33.611 33.3678 27.4657 33.5243 27.2029C33.6746 26.9523 33.6269 26.6309 33.4094 26.4353ZM9.77647 18.3332C9.59314 18.3332 9.40614 18.2917 9.23136 18.2037C8.62758 17.9018 8.38314 17.1672 8.68503 16.5634C8.74858 16.4363 10.2959 13.4443 14.6666 13.4443C19.0385 13.4443 20.5846 16.4376 20.6481 16.5647C20.95 17.1684 20.7056 17.903 20.1018 18.2049C19.5005 18.5043 18.7757 18.2648 18.4689 17.672C18.4164 17.5742 17.4545 15.8888 14.6666 15.8888C11.8347 15.8888 10.8789 17.6402 10.8704 17.6573C10.6565 18.0863 10.225 18.3332 9.77647 18.3332ZM34.2234 18.3332C33.7748 18.3332 33.3434 18.0863 33.1283 17.6573C33.0977 17.6011 32.1431 15.8888 29.3333 15.8888C26.5234 15.8888 25.5688 17.6011 25.5297 17.6732C25.2119 18.2611 24.4798 18.4921 23.8858 18.1866C23.293 17.8786 23.0523 17.1611 23.3505 16.5634C23.414 16.4376 24.9614 13.4443 29.3333 13.4443C33.7051 13.4443 35.2525 16.4376 35.316 16.5647C35.6179 17.1684 35.3735 17.903 34.7697 18.2049C34.5937 18.2917 34.4067 18.3332 34.2234 18.3332Z"
            fill="#664500"
          />
        </Svg>
      );
    case "neutral-face":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 44 44"
          fill="none"
          {...props}
        >
          <Path
            d="M44 22C44 34.1501 34.1501 44 22 44C9.85111 44 0 34.1501 0 22C0 9.85111 9.85111 0 22 0C34.1501 0 44 9.85111 44 22Z"
            fill="#FFCC4D"
          />
          <Path
            d="M14.0556 24.4442C15.7431 24.4442 17.1111 22.529 17.1111 20.1664C17.1111 17.8039 15.7431 15.8887 14.0556 15.8887C12.368 15.8887 11 17.8039 11 20.1664C11 22.529 12.368 24.4442 14.0556 24.4442Z"
            fill="#664500"
          />
          <Path
            d="M29.9442 24.4442C31.6318 24.4442 32.9998 22.529 32.9998 20.1664C32.9998 17.8039 31.6318 15.8887 29.9442 15.8887C28.2567 15.8887 26.8887 17.8039 26.8887 20.1664C26.8887 22.529 28.2567 24.4442 29.9442 24.4442Z"
            fill="#664500"
          />
          <Path
            d="M30.5555 31.7775H13.4444C12.7697 31.7775 12.2222 31.2311 12.2222 30.5552C12.2222 29.8793 12.7697 29.333 13.4444 29.333H30.5555C31.2314 29.333 31.7777 29.8793 31.7777 30.5552C31.7777 31.2311 31.2314 31.7775 30.5555 31.7775Z"
            fill="#664500"
          />
        </Svg>
      );
    case "language":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            fill={color}
            d="M12 1.18c-5.946 0-10.82 4.403-10.82 9.88 0 2.327.898 4.46 2.368 6.144h-.015c.184.308.208.676.103 1.132-.105.455-.34.968-.618 1.44-.277.473-.588.899-.837 1.235-.125.167-.24.309-.324.426-.042.06-.07.12-.103.177-.033.057-.103.053-.103.308a.88.88 0 0 0 .265.618c.187.184.457.28.764.28 2.169 0 4.142-1.612 5.645-2.5 1.152.382 2.382.618 3.675.618 5.946 0 10.82-4.403 10.82-9.879S17.945 1.181 12 1.181Zm0 .942c5.485 0 9.878 4.02 9.878 8.937S17.485 19.997 12 19.997a10.78 10.78 0 0 1-3.601-.618l-.206-.073-.177.117c-1.631.934-3.596 2.439-5.306 2.455.064-.088.127-.169.22-.294.256-.343.59-.816.897-1.337.307-.522.597-1.101.735-1.706.138-.604.121-1.25-.22-1.822l-.03-.044-.015-.03c-1.36-1.536-2.175-3.473-2.175-5.586 0-4.917 4.393-8.937 9.878-8.937ZM7.928 5.885a.473.473 0 0 0-.367.323l-1.426 4.116a.47.47 0 0 0-.088.265l-.603 1.734a.471.471 0 1 0 .882.294l.544-1.558h2.264l.544 1.558a.47.47 0 1 0 .881-.294L8.444 6.208a.47.47 0 0 0-.515-.323Zm.074 1.896.808 2.337H7.193l.809-2.337Zm7.717 1.382a.55.55 0 0 0-.059.015.471.471 0 0 0-.367.47v.94h-2.44a.473.473 0 0 0 .088.942h4.101a6.348 6.348 0 0 1-1.558 2.616c-.801-.79-1.161-1.455-1.161-1.455a.47.47 0 1 0-.824.456s.407.733 1.265 1.602a7.09 7.09 0 0 1-1.97 1.044.47.47 0 1 0 .294.882 7.836 7.836 0 0 0 2.367-1.294 9.415 9.415 0 0 0 1.984 1.279.474.474 0 0 0 .412-.853 8.703 8.703 0 0 1-1.69-1.058 7.538 7.538 0 0 0 1.851-3.22h.574a.473.473 0 0 0 .413-.233.474.474 0 0 0 0-.474.473.473 0 0 0-.413-.233h-2.352v-.941a.468.468 0 0 0-.515-.485Z"
          />
        </Svg>
      );
    case "about-app":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <G clipPath="url(#a)">
            <Path
              fill={color}
              d="M12 .71C5.52.71.24 5.357.24 11.147c0 2.845 1.305 5.399 3.396 7.292-.316.975-1.022 1.745-1.735 2.337-.384.318-.751.59-1.044.852a2.121 2.121 0 0 0-.382.427c-.105.163-.198.397-.132.647l.059.19.161.104c.607.349 1.362.41 2.176.352.814-.056 1.702-.244 2.572-.485 1.691-.466 3.279-1.132 4.116-1.5.827.174 1.658.28 2.529.28 6.48 0 11.76-4.647 11.76-10.437C23.716 5.418 18.483.71 12 .71Zm0 .941c6.03 0 10.775 4.335 10.775 9.555 0 5.217-4.787 9.496-10.82 9.496-.86 0-1.67-.084-2.483-.264l-.162-.044-.132.073c-.787.348-2.45 1.026-4.116 1.485-.835.23-1.665.406-2.382.456-.48.033-.775-.081-1.102-.162.233-.2.553-.44.926-.75.82-.68 1.802-1.626 2.146-3.028l.073-.294-.235-.176c-2.072-1.757-3.307-4.175-3.307-6.85C1.18 5.93 5.967 1.65 12 1.65Zm-.559 4.616c-.141 0-.19.083-.19.176v1.235c0 .142.097.177.19.177h1.118c.141 0 .19-.083.19-.177V6.443c0-.141-.097-.176-.19-.176H11.44Zm.044 3.425c-.141 0-.19.098-.19.191v6.306c0 .142.097.192.19.192v.044h1.074c.141 0 .19-.098.19-.191v-6.35c0-.142-.097-.192-.19-.192h-1.073Z"
            />
          </G>
          <Defs>
            <ClipPath id="a">
              <Path fill="#fff" d="M0 0h24v24H0z" />
            </ClipPath>
          </Defs>
        </Svg>
      );
    case "legal":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M3.533 1.651V22.35h15.32l-.845-.941H4.475V2.592h10.348v4.704h4.704v3.918l.941.941V6.631l-4.98-4.98H3.534Zm12.23 1.606 3.098 3.098h-3.098V3.257Zm-.604 5.454c-.328 0-.655.123-.903.37a1.284 1.284 0 0 0 0 1.806l.052.052-3.37 3.368-.05-.051a1.273 1.273 0 0 0-.904-.37c-.327 0-.655.123-.902.37a1.284 1.284 0 0 0 0 1.806l4.031 4.031a1.278 1.278 0 0 0 1.806 0 1.282 1.282 0 0 0 0-1.805l-.023-.023.831-.832 5.275 5.898.009.01c.618.617 1.638.558 2.263-.067.624-.625.684-1.645.066-2.263l-.01-.009-5.897-5.275.832-.831.023.023a1.278 1.278 0 0 0 1.805 0 1.282 1.282 0 0 0 0-1.805L16.062 9.08a1.273 1.273 0 0 0-.903-.37Zm0 .934c.085 0 .17.034.237.102l4.032 4.032a.329.329 0 0 1 0 .475.33.33 0 0 1-.238.098.326.326 0 0 1-.237-.098l-4.032-4.033a.328.328 0 0 1 0-.474.336.336 0 0 1 .239-.102Zm-.186 1.96L17.6 14.23l-3.37 3.37-2.626-2.627 3.369-3.369ZM9.985 14.82c.085 0 .17.034.237.102l4.032 4.031a.329.329 0 0 1 0 .475.33.33 0 0 1-.238.098.326.326 0 0 1-.237-.098l-4.033-4.032a.328.328 0 0 1 0-.474h.001a.336.336 0 0 1 .238-.102Zm6.782 1.573 5.909 5.286c.225.228.226.637-.067.93-.293.293-.702.292-.93.067l-5.286-5.909.374-.373Z"
          />
        </Svg>
      );
    case "notification":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M12 3.062c-.774 0-1.411.638-1.411 1.412v.661c-.915.215-1.715.64-2.33 1.259-.919.928-1.433 2.242-1.433 3.754v3.67c0 .863-.473 1.958-.96 2.77l-.843 1.266a.475.475 0 0 0-.024.483.474.474 0 0 0 .415.249h4.234A2.361 2.361 0 0 0 12 20.938a2.361 2.361 0 0 0 2.352-2.352h4.234a.474.474 0 0 0 .415-.248.475.475 0 0 0-.024-.484l-.84-1.26-.002-.002c-.492-.821-.96-1.847-.96-2.71v-3.67c0-2.525-1.504-4.48-3.764-5.06v-.678c0-.774-.637-1.412-1.411-1.412Zm0 .941c.26 0 .47.21.47.47v.524c-.104-.009-.207-.02-.314-.024a.41.41 0 0 0-.305-.002c-.11.002-.215.013-.321.02v-.517c0-.261.21-.47.47-.47Zm-10.123.695C.828 6.403.24 8.433.24 10.588c0 2.156.588 4.187 1.637 5.892l.803-.493c-.957-1.556-1.5-3.414-1.5-5.398 0-1.985.543-3.842 1.5-5.399l-.803-.492Zm20.246 0-.803.492c.957 1.557 1.5 3.414 1.5 5.399 0 1.984-.543 3.842-1.5 5.398l.803.493c1.049-1.705 1.637-3.736 1.637-5.891 0-2.156-.588-4.186-1.637-5.891ZM12 5.885c2.593 0 4.234 1.725 4.234 4.327v3.67c0 1.181.565 2.313 1.095 3.197.005.005.009.013.013.018l.363.548H6.295l.363-.548c.004-.005.008-.013.013-.018.535-.891 1.095-2.08 1.095-3.262v-3.67c0-1.3.43-2.353 1.162-3.09C9.658 6.32 10.7 5.885 12 5.885Zm-8.083.066a8.653 8.653 0 0 0-1.325 4.638c0 1.727.531 3.315 1.321 4.632l.807-.485c-.713-1.19-1.187-2.613-1.187-4.147 0-1.547.41-2.892 1.183-4.144l-.8-.494Zm16.166 0-.8.494a7.656 7.656 0 0 1 1.184 4.144c0 1.534-.474 2.956-1.189 4.147l.809.485c.79-1.317 1.321-2.905 1.321-4.632a8.654 8.654 0 0 0-1.325-4.638ZM10.59 18.586h2.822a1.412 1.412 0 0 1-2.822 0Z"
          />
        </Svg>
      );
    case "blocked-user":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M12 1.651c-2.735 0-4.545 1.687-4.694 4.351-.006.116-.01.234-.01.353 0 .775.19 1.552.402 2.219-.203.242-.4.61-.402 1.071 0 .05.002.163.008.237.13.992.578 1.465.972 1.656.13.893.454 1.641.902 2.086V15.4c-.272.652-1.218 1.03-2.31 1.465-1.834.733-4.118 1.644-4.275 4.517l-.001.496h11.583a5.573 5.573 0 0 1-.435-.94H3.59c.344-1.888 2.01-2.554 3.627-3.2 1.287-.514 2.504-.998 2.877-2.102l.024-.074v-2.386l-.188-.141c-.31-.233-.664-.912-.754-1.901l-.025-.427h-.43c-.095-.017-.39-.227-.481-.924a2.537 2.537 0 0 1-.003-.132c.002-.356.267-.556.269-.558l.289-.202-.113-.334c-.317-.946-.446-1.58-.445-2.185C8.239 4.04 9.68 2.592 12 2.592c.93 0 1.465.395 1.64.733l.116.22.247.03c1.442.17 1.753 1.45 1.76 2.493.007 1.013-.3 2.018-.449 2.501l-.08.363.308.19c.043.028.22.207.222.53 0 .03-.001.1-.003.13-.066.757-.385.922-.467.925h-.41l-.057.406c-.151 1.112-.49 1.723-.757 1.923l-.188.14v2.38l.03.08c.075.197.183.368.305.529a5.76 5.76 0 0 1 .605-.83v-1.712c.521-.528.77-1.412.887-2.063.427-.178.898-.656.989-1.7a3.92 3.92 0 0 0 .006-.216 1.674 1.674 0 0 0-.402-1.094c.172-.586.409-1.517.402-2.49-.013-1.885-.87-3.105-2.36-3.384C13.89 2.038 13.019 1.65 12 1.65Zm7.056 12.701a4.711 4.711 0 0 0-4.704 4.704 4.711 4.711 0 0 0 4.704 4.704 4.711 4.711 0 0 0 4.704-4.704 4.711 4.711 0 0 0-4.704-4.704Zm0 .94a3.756 3.756 0 0 1 3.763 3.764 3.756 3.756 0 0 1-3.763 3.763 3.756 3.756 0 0 1-3.763-3.763 3.756 3.756 0 0 1 3.763-3.763Zm-1.651 1.643a.47.47 0 0 0-.328.807l1.314 1.314-1.314 1.314a.47.47 0 1 0 .665.665l1.314-1.314 1.314 1.314a.47.47 0 1 0 .665-.665l-1.314-1.314 1.314-1.314a.47.47 0 1 0-.665-.665l-1.314 1.314-1.314-1.314a.47.47 0 0 0-.337-.142Z"
          />
        </Svg>
      );
    case "feedback":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            fill={color}
            d="M6.12 1.18c-.722 0-1.038.613-1.066.671L2.188 6.076v.002a2.9 2.9 0 0 0-.537 1.688v3.058a2.593 2.593 0 0 0 2.587 2.587h5.41c.774 0 1.411-.637 1.411-1.411v-.29c.191-.188.47-.522.47-1.121a1.6 1.6 0 0 0-.317-.941c.16-.224.318-.535.318-.94 0-.6-.28-.934-.47-1.122v-.29a1.42 1.42 0 0 0-1.412-1.411H6.59c-.25 0-.213-.028-.215-.031-.002-.006-.022-.157.068-.39.65-1.72.853-2.473.853-3.107 0-.627-.524-1.176-1.176-1.176Zm0 .942c.17 0 .235.082.235.235 0 .424-.15 1.08-.794 2.773-.134.358-.213.736-.042 1.113.173.377.616.583 1.071.583h3.058c.266 0 .47.204.47.47v.47c0 .15.07.289.19.377 0 0 .28.228.28.564 0 .33-.266.544-.277.553a.474.474 0 0 0-.198.417v.002a.475.475 0 0 0 .2.358c.011.01.276.221.276.552 0 .336-.281.564-.281.564a.467.467 0 0 0-.19.377V12c0 .266-.204.47-.47.47h-5.41a1.639 1.639 0 0 1-1.646-1.646V7.766a1.916 1.916 0 0 1 .367-1.154l2.883-4.244a.194.194 0 0 0 .02-.033s.192-.213.258-.213Zm8.232 8.467c-.774 0-1.411.637-1.411 1.411v.29c-.191.188-.47.522-.47 1.121 0 .406.157.717.317.941-.16.224-.318.535-.318.94 0 .6.28.934.47 1.122v.29c0 .774.638 1.411 1.412 1.411h3.058c.25 0 .213.028.215.031a.828.828 0 0 1-.069.39c-.65 1.72-.852 2.473-.852 3.107 0 .627.524 1.176 1.176 1.176.722 0 1.038-.612 1.066-.67l2.866-4.225v-.002a2.9 2.9 0 0 0 .537-1.688v-3.058a2.593 2.593 0 0 0-2.587-2.587h-5.41Zm0 .94h5.41a1.64 1.64 0 0 1 1.646 1.647v3.058a1.927 1.927 0 0 1-.368 1.153l-2.883 4.245a.308.308 0 0 0-.02.033s-.19.213-.257.213c-.171 0-.235-.082-.235-.235 0-.424.15-1.08.794-2.773.134-.358.213-.736.042-1.113-.173-.377-.616-.583-1.072-.583h-3.057a.462.462 0 0 1-.47-.47v-.47a.467.467 0 0 0-.19-.377s-.28-.228-.28-.564c0-.331.266-.544.277-.553a.474.474 0 0 0 .198-.417v-.002a.476.476 0 0 0-.198-.357c-.002 0-.002 0-.002-.002-.011-.009-.276-.22-.276-.55 0-.337.281-.565.281-.565a.467.467 0 0 0 .19-.377V12c0-.267.204-.47.47-.47Z"
          />
        </Svg>
      );
    case "delete-account":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M10.118.24c-.773 0-1.41.638-1.41 1.411v.941H5.031a.474.474 0 0 0-.176 0h-.897a.472.472 0 0 0 .044.94h.515l1.69 19.052c.06.658.62 1.176 1.28 1.176h9.025c.66 0 1.22-.518 1.279-1.176l1.69-19.051h.515a.473.473 0 0 0 .413-.234.474.474 0 0 0 0-.474.473.473 0 0 0-.413-.233h-4.704v-.94A1.42 1.42 0 0 0 13.881.24h-3.764Zm0 .94h3.764c.264 0 .47.207.47.471v.941H9.648v-.94c0-.265.206-.471.47-.471Zm-4.66 2.353h13.084l-1.69 18.978c-.016.163-.19.308-.339.308H7.487c-.149 0-.323-.145-.338-.308L5.46 3.532Zm3.676 1.396a.55.55 0 0 0-.06.015.471.471 0 0 0-.367.47v15.524a.473.473 0 0 0 .234.413.474.474 0 0 0 .474 0 .473.473 0 0 0 .233-.413V5.414a.468.468 0 0 0-.514-.485Zm2.822 0a.551.551 0 0 0-.059.015.471.471 0 0 0-.367.47v15.524a.473.473 0 0 0 .233.413.475.475 0 0 0 .474 0 .473.473 0 0 0 .233-.413V5.414a.468.468 0 0 0-.514-.485Zm2.822 0a.551.551 0 0 0-.058.015.471.471 0 0 0-.368.47v15.524a.473.473 0 0 0 .233.413.475.475 0 0 0 .474 0 .473.473 0 0 0 .234-.413V5.414a.468.468 0 0 0-.515-.485Z"
          />
        </Svg>
      );
    case "name":
      return (
        <Svg
          x="0px"
          y="0px"
          width={size}
          height={size}
          viewBox="0,0,256,256"
          fill={color}
        >
          <G transform="translate(2.56,2.56) scale(0.98,0.98)">
            <G transform="scale(5.12,5.12)">
              <Path d="M41.26953,1.98828c-0.77823,0.03161 -1.50956,0.51848 -1.80078,1.28516c-0.00066,0.00195 -0.00131,0.0039 -0.00195,0.00586l-0.83789,2.23828l-0.85547,-0.32617c-1.48817,-0.56562 -3.18407,0.19339 -3.74609,1.68555c-1.49573,3.97133 -4.18762,10.9448 -4.67969,12.42969c-0.12585,0.37807 -0.35438,0.49928 -0.68164,0.60156c-0.32726,0.10228 -0.61523,0.0918 -0.61523,0.0918c-0.35696,-0.01821 -0.69651,0.15542 -0.89072,0.45547c-0.19421,0.30005 -0.21357,0.68093 -0.05079,0.99913c0.16279,0.3182 0.48298,0.52537 0.83995,0.54344c0,0 0.61547,0.03682 1.31445,-0.18164c0.69899,-0.21847 1.61928,-0.78603 1.98242,-1.87695c0,-0.00065 0,-0.0013 0,-0.00195c0.38247,-1.15442 3.15224,-8.36735 4.6543,-12.35547c0.17998,-0.47784 0.68423,-0.70386 1.16406,-0.52148l0.86523,0.32813l-11.33789,30.30273c-0.01747,0.04835 -0.03118,0.09798 -0.04102,0.14844c-0.0262,0.09998 -0.03674,0.20343 -0.03125,0.30664l0.2793,6.70703l-0.72461,1.76758c-0.13549,0.33077 -0.08419,0.70871 0.13458,0.9914c0.21877,0.28269 0.57175,0.42715 0.92594,0.37897c0.35419,-0.04818 0.65575,-0.2817 0.79105,-0.61255l0.63086,-1.53906l5.20508,-4.89453c0.16844,-0.15842 0.2768,-0.37031 0.30664,-0.59961l12.8125,-34.24609c0.38751,-1.02019 -0.13583,-2.18877 -1.15625,-2.57813l-0.00195,-0.00195l-3.67187,-1.4043c-0.00065,0 -0.0013,0 -0.00195,0c-0.25528,-0.09697 -0.51989,-0.13749 -0.7793,-0.12695zM41.33789,3.98438l3.67383,1.4043c0,0.00065 0,0.0013 0,0.00195c-0.00065,0.0013 -0.0013,0.0026 -0.00195,0.00391l-0.83398,2.23047l-3.67773,-1.39844zM39.79883,8.09961l3.67578,1.39844l-3.76562,10.0625l-3.66406,-1.43164zM13,18c-1.5348,0 -2.89762,0.66972 -4.00977,1.64063c-1.11215,0.9709 -2.01579,2.24612 -2.77344,3.61523c-1.5153,2.73822 -2.4397,5.86252 -2.85156,7.82227c-0.00139,0.0078 -0.0027,0.01561 -0.00391,0.02344c-0.18346,0.98751 -0.53243,3.44388 -0.31055,5.81445c0.11094,1.18529 0.35262,2.3542 0.94336,3.33594c0.59075,0.98173 1.67661,1.74805 3.00586,1.74805c2.28325,0 3.83179,-1.03054 4.88672,-1.89062c0.52747,-0.43004 0.95525,-0.8129 1.28516,-1.02539c0.14377,-0.0926 0.20591,-0.06329 0.30273,-0.09961c0.05628,0.39642 0.09256,0.9688 0.1543,1.59375c0.06574,0.66553 0.15071,1.36378 0.4375,2.0293c0.1434,0.33276 0.34618,0.66991 0.67578,0.94336c0.3296,0.27345 0.79386,0.4467 1.25195,0.44922c0.58856,0.0031 1.0124,-0.26183 1.34375,-0.51172c0.33135,-0.24989 0.60217,-0.52793 0.85352,-0.77734c0.25134,-0.24941 0.48233,-0.4708 0.65039,-0.5957c0.16806,-0.1249 0.24237,-0.11523 0.1582,-0.11523c0.01574,0 0.01361,-0.00493 0.02539,-0.00586c0.0418,0.1016 0.12861,0.47637 0.26758,1.01563c0.29022,1.12618 1.06245,2.80499 3.33594,3.96484c0.49242,0.25079 1.09491,0.05492 1.3457,-0.4375c0.25079,-0.49242 0.05492,-1.09491 -0.4375,-1.3457c-1.80951,-0.92315 -2.07806,-1.78511 -2.30859,-2.67969c-0.11527,-0.44729 -0.15152,-0.90884 -0.42773,-1.45898c-0.27621,-0.55015 -1.05078,-1.05273 -1.80078,-1.05273c-0.58417,0 -1.014,0.25889 -1.35156,0.50977c-0.33756,0.25088 -0.61403,0.53199 -0.86719,0.7832c-0.25316,0.25121 -0.48316,0.47301 -0.64844,0.59766c-0.06888,0.05194 -0.10815,0.0703 -0.13477,0.08398c-0.01515,-0.01608 -0.04721,-0.0457 -0.0957,-0.1582c-0.11946,-0.2772 -0.22199,-0.83571 -0.28125,-1.43555c-0.05926,-0.59984 -0.08655,-1.23617 -0.17969,-1.81641c-0.04657,-0.29012 -0.10061,-0.56644 -0.25,-0.875c-0.14939,-0.30856 -0.51792,-0.742 -1.05859,-0.81445c-0.78545,-0.10475 -1.4991,0.17705 -2.04297,0.52734c-0.54387,0.3503 -0.99264,0.76967 -1.4668,1.15625c-0.94832,0.77316 -1.9063,1.44141 -3.62305,1.44141c-0.67075,0 -0.95806,-0.22272 -1.29297,-0.7793c-0.33491,-0.55658 -0.57083,-1.47522 -0.66602,-2.49219c-0.19023,-2.03242 0.13343,-4.42961 0.28711,-5.25781c0.00011,-0.00061 -0.00011,-0.00331 0,-0.00391c0.37648,-1.78259 1.27718,-4.78191 2.63867,-7.24219c0.6831,-1.23439 1.48305,-2.33015 2.33984,-3.07812c0.85679,-0.74798 1.72816,-1.14648 2.69336,-1.14648c1.16667,0 1.75328,0.46724 2.25,1.36133c0.49672,0.89409 0.75,2.24978 0.75,3.63867c-0.0051,0.36064 0.18438,0.69608 0.49587,0.87789c0.3115,0.18181 0.69676,0.18181 1.00825,0c0.3115,-0.18181 0.50097,-0.51725 0.49587,-0.87789c0,-1.61111 -0.24672,-3.25542 -1,-4.61133c-0.75328,-1.35591 -2.16667,-2.38867 -4,-2.38867zM35.34375,20.00195l3.66406,1.43164l-6.52148,17.42969l-3.67383,-1.40234zM28.57813,39.51172l2.69531,1.02734l-2.55273,2.40039z" />
            </G>
          </G>
        </Svg>
      );
    case "date-of-birth":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M5.885.24a.947.947 0 0 0-.94.94v.942H2.121c-.2 0-.406.046-.596.18a.848.848 0 0 0-.345.663v18.934c0 .492.399.92.902.92h19.834c.505 0 .902-.428.902-.92V2.965a.848.848 0 0 0-.345-.663 1.004 1.004 0 0 0-.596-.18h-2.822V1.18a.947.947 0 0 0-.94-.941h-.942a.947.947 0 0 0-.94.94v.942H7.767V1.18a.947.947 0 0 0-.941-.94h-.941Zm0 .94h.94v2.823h-.94V1.181Zm11.29 0h.94v2.823h-.94V1.181ZM2.121 3.063h2.822v.941c0 .515.426.941.94.941h.942c.514 0 .94-.426.94-.94v-.942h8.468v.941c0 .515.426.941.94.941h.941a.947.947 0 0 0 .941-.94v-.942h2.822v3.293H2.122V3.062Zm0 4.234h19.756v14.582H2.122V7.296Zm9.87 2.352a.469.469 0 0 0-.325.138l-2.822 2.822a.47.47 0 0 0 .333.803h5.644a.47.47 0 0 0 .333-.803l-2.822-2.822a.472.472 0 0 0-.34-.138ZM12 10.784l1.687 1.686h-3.374L12 10.784Zm-2.822 4.509a.47.47 0 0 0-.333.803l2.823 2.822a.47.47 0 0 0 .665 0l2.822-2.822a.47.47 0 0 0-.332-.803H9.178Zm1.135.94h3.374L12 17.92l-1.687-1.686Z"
          />
        </Svg>
      );
    case "gender":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            fill={color}
            d="M17.645 1.18v.942h4.498L18.13 6.135a5.151 5.151 0 0 0-3.308-1.191 5.159 5.159 0 0 0-5.174 5.174c0 2.117 1.312 3.95 3.146 4.748.093-.328.147-.655.147-.984-1.411-.706-2.352-2.117-2.352-3.764a4.216 4.216 0 0 1 4.233-4.233 4.216 4.216 0 0 1 4.234 4.233 4.216 4.216 0 0 1-4.234 4.234h-.044c-.048.329-.099.612-.147.94h.191a5.159 5.159 0 0 0 5.175-5.174c0-1.262-.447-2.41-1.19-3.307l4.012-4.013v4.498h.941V1.181h-6.115ZM9.178 8.708a5.159 5.159 0 0 0-5.175 5.175c0 1.262.447 2.41 1.19 3.307l-2.13 2.132-2.014-2.014-.677.676 2.014 2.014L.372 22.01l.677.676 2.013-2.014 2.014 2.014.677-.676-2.014-2.014 2.131-2.132a5.151 5.151 0 0 0 3.308 1.191 5.159 5.159 0 0 0 5.174-5.174c0-2.117-1.312-3.95-3.146-4.749a3.113 3.113 0 0 0-.147.985c1.411.706 2.352 2.117 2.352 3.764a4.216 4.216 0 0 1-4.233 4.233 4.216 4.216 0 0 1-4.234-4.233 4.216 4.216 0 0 1 4.234-4.234h.044c.047-.329.099-.612.147-.94h-.191Z"
          />
        </Svg>
      );
    case "interested-in":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            fill={color}
            d="M10.08 1.44c-4.501 0-8.16 3.659-8.16 8.16 0 4.5 3.659 8.16 8.16 8.16 1.782 0 3.427-.58 4.77-1.553l6.311 6.312 1.358-1.358-6.253-6.253A8.119 8.119 0 0 0 18.24 9.6c0-4.501-3.66-8.16-8.16-8.16Zm0 .96c3.982 0 7.2 3.218 7.2 7.2s-3.218 7.2-7.2 7.2a7.193 7.193 0 0 1-7.2-7.2c0-3.982 3.218-7.2 7.2-7.2ZM8.384 6.24C6.9 6.24 5.76 7.376 5.76 8.86c0 .819.335 1.508.786 2.07.45.56 1.013 1.017 1.542 1.452v-.001c.484.399 1.68 1.424 1.68 1.424a.48.48 0 0 0 .625 0s1.196-1.025 1.68-1.423c.528-.435 1.091-.892 1.541-1.453.45-.56.786-1.25.786-2.07 0-1.483-1.14-2.62-2.624-2.62-.75 0-1.29.383-1.697.743-.407-.36-.945-.742-1.695-.742Zm0 .96c.496 0 1.048.416 1.346.73a.48.48 0 0 0 .696.001c.3-.315.854-.731 1.35-.731 1.038 0 1.664.624 1.664 1.66 0 .566-.213 1.018-.575 1.468-.362.451-.874.877-1.402 1.312-.415.341-1.078.91-1.383 1.17-.306-.26-.969-.83-1.382-1.17-.53-.435-1.043-.861-1.404-1.312-.362-.45-.574-.902-.574-1.469 0-1.035.626-1.66 1.664-1.66Z"
          />
        </Svg>
      );
    case "bio":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M11.06 2.592a.947.947 0 0 0-.942.94v2.353H3.377c-1.198 0-2.196.95-2.196 2.131v10.79c0 1.182.998 2.132 2.196 2.132h17.247c1.198 0 2.195-.95 2.195-2.132V8.016c0-1.181-.997-2.131-2.195-2.131h-6.272V3.533a.947.947 0 0 0-.94-.941h-2.353Zm0 .94h2.351v2.744a.425.425 0 0 0 0 .153v.867H11.06v-.862a.425.425 0 0 0 0-.152v-2.75ZM3.376 6.827h6.741v.47c0 .515.427.94.941.94h2.352a.946.946 0 0 0 .941-.94v-.47h6.271c.708 0 1.255.536 1.255 1.19v10.79c0 .654-.547 1.19-1.255 1.19H3.378c-.708 0-1.255-.536-1.255-1.19V8.016c0-.654.547-1.19 1.255-1.19Zm3.9 1.881c-.96 0-1.686.447-1.991 1.117-.274.601-.235 1.305-.08 2.016a.909.909 0 0 0-.13.588c.035.276.11.498.248.673.043.055.113.051.164.092.046.194.092.391.173.55.055.111.116.212.182.302.007.011.024.019.029.028.002.167.002.33-.01.5-.03.062-.1.145-.31.248-.226.112-.55.224-.895.369-.345.147-.714.334-1.025.645a2.02 2.02 0 0 0-.57 1.31v.5c0 .259.212.47.47.47h7.527a.47.47 0 0 0 .47-.47v-.5a2.017 2.017 0 0 0-.567-1.308c-.309-.313-.678-.498-1.02-.647-.344-.147-.669-.257-.893-.37-.208-.102-.28-.185-.31-.248a6.538 6.538 0 0 1-.01-.501c.008-.011.024-.02.03-.03a2.02 2.02 0 0 0 .182-.3c.077-.16.12-.357.163-.55.054-.039.121-.037.164-.09.138-.175.213-.397.248-.673a.909.909 0 0 0-.134-.593c.095-.338.216-.752.176-1.268-.024-.316-.105-.65-.316-.941-.191-.265-.51-.45-.882-.53-.29-.264-.669-.389-1.082-.389Zm0 .941c.355 0 .506.121.499.11.073.125.2.212.343.232.215.03.292.094.362.19.07.095.123.256.138.46.033.408-.092.945-.169 1.176a.465.465 0 0 0 .143.51c.002.005-.01-.02-.012-.01-.017.134-.04.162-.043.171a.468.468 0 0 0-.303.39 1.525 1.525 0 0 1-.235.61c-.017.022-.02.022-.004.01a.469.469 0 0 0-.211.392c0 .28-.011.524.02.873.004.04.013.082.028.121.15.4.485.625.797.78.313.155.643.266.94.392.299.127.552.27.723.443.164.164.26.357.285.676H4.014c.026-.317.122-.51.287-.676.169-.172.428-.314.726-.44.297-.13.63-.24.944-.396.313-.154.649-.378.798-.779a.404.404 0 0 0 .03-.121c.03-.35.02-.594.02-.873a.471.471 0 0 0-.217-.395c.02.013.012.013-.004-.011a1.502 1.502 0 0 1-.24-.606.472.472 0 0 0-.304-.39c-.004-.01-.026-.035-.042-.17-.002-.017-.02.016-.017.012a.469.469 0 0 0 .156-.483c-.174-.675-.17-1.276-.009-1.632.162-.355.42-.566 1.136-.566Zm5.193.47v.941h8.468v-.94H12.47Zm0 1.882v.94h6.586V12H12.47Zm0 1.882v.94h5.175v-.94H12.47Zm0 1.881v.941h6.586v-.94H12.47Z"
          />
        </Svg>
      );
    case "profile-picture":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <G clipPath="url(#a)">
            <Path
              fill={color}
              d="M11.809.24c-1.553 0-2.393.94-2.676 1.411-2.164.235-3.719 2.817-3.719 6.204 0 .988-.003 1.7.044 2.263H6.4c0-.518-.044-1.275-.044-2.263 0-2.964 1.318-5.263 3.058-5.263.187 0 .332-.092.426-.28 0 0 .51-1.131 1.97-1.131 4.845 0 6.119 4.656 6.306 8.937h.941C18.821 3.72 16.278.24 11.809.24Zm-1.646 3.763c-.188 0-.333.05-.427.191-1.411 2.023-1.828 3.855-1.264 5.924h.985c-.518-1.646-.327-3.15.661-4.748.941 1.129 2.87 2.165 4.234 2.352 0 .989-.004 1.785.044 2.396h.94c0-.657-.043-1.646-.043-2.822 0-.283-.188-.47-.47-.47-1.412 0-3.856-1.461-4.278-2.544a.416.416 0 0 0-.383-.279Zm-.045 3.999a.706.706 0 1 0 0 1.411.706.706 0 0 0 0-1.411Zm3.293 0a.706.706 0 1 0 0 1.411.706.706 0 0 0 0-1.411Zm-11.29 2.587A1.88 1.88 0 0 0 .24 12.47v9.408a1.88 1.88 0 0 0 1.882 1.882h19.756a1.88 1.88 0 0 0 1.882-1.882V12.47a1.88 1.88 0 0 0-1.882-1.881H2.122Zm0 .94h19.757c.56 0 .941.381.941.941v9.408c0 .56-.38.941-.94.941H2.121c-.56 0-.941-.38-.941-.94V12.47c0-.56.38-.94.94-.94Zm.941.941a.957.957 0 0 0-.676.265.957.957 0 0 0-.264.676v7.527c0 .246.086.498.264.676.178.178.43.264.676.264h16.935a.957.957 0 0 0 .676-.264.957.957 0 0 0 .265-.676V13.41a.957.957 0 0 0-.265-.676.957.957 0 0 0-.676-.265H3.062Zm0 .941h16.935v7.527h-3.058c-.031-1.783-.32-3.451-1.043-4.748-.769-1.377-2.099-2.308-3.94-2.308-1.205 0-1.72.758-1.882 1.029-.726.11-1.369.545-1.793 1.234-.482.783-.75 1.82-.75 3 0 .94.017 1.465.03 1.793H3.062V13.41Zm8.894 1.411c1.545 0 2.473.671 3.116 1.823.605 1.082.895 2.61.926 4.293h-1.514a44.03 44.03 0 0 1-.044-2.161.47.47 0 0 0-.47-.47c-.368 0-1.038-.217-1.603-.545-.564-.327-1.014-.788-1.102-1.029a.47.47 0 0 0-.53-.308.471.471 0 0 0-.293.19c-1.106 1.55-1.202 2.998-.794 4.323H8.501a39.61 39.61 0 0 1-.029-1.794c0-1.032.252-1.905.617-2.499.366-.593.802-.882 1.265-.882a.472.472 0 0 0 .426-.28s.252-.66 1.176-.66Zm9.922 1.647a.706.706 0 1 0 0 1.411.706.706 0 0 0 0-1.411Zm-11.01 1.279c.29.312.628.591 1.03.823a4.825 4.825 0 0 0 1.616.588c.01.937.037 1.521.044 1.779h-2.925c-.399-1.04-.425-2.005.235-3.19Zm.191 1.543a.471.471 0 0 0 0 .941.471.471 0 0 0 0-.94Zm1.882 0a.471.471 0 0 0 0 .941.471.471 0 0 0 0-.94Z"
            />
          </G>
          <Defs>
            <ClipPath id="a">
              <Path fill="#fff" d="M0 0h24v24H0z" />
            </ClipPath>
          </Defs>
        </Svg>
      );
    case "video":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 24 24">
          <Path
            fill={color}
            d="M1.18 2.122a.47.47 0 0 0-.47.47v2.273a.47.47 0 0 0 0 .153v13.959a.47.47 0 0 0 0 .153v2.278c0 .26.211.47.47.47h21.64a.47.47 0 0 0 .47-.47v-2.273a.47.47 0 0 0 0-.153V5.023a.47.47 0 0 0 0-.153V2.592a.47.47 0 0 0-.47-.47H1.18Zm.471.94h1.411v1.412h-1.41V3.062Zm2.352 0h1.882v1.412H4.003V3.062Zm2.823 0h1.881v1.412H6.826V3.062Zm2.822 0h1.882v1.412H9.648V3.062Zm2.822 0h1.882v1.412H12.47V3.062Zm2.823 0h1.881v1.412h-1.881V3.062Zm2.822 0h1.882v1.412h-1.882V3.062Zm2.823 0h1.41v1.412h-1.41V3.062ZM1.65 5.414h20.7v13.172H1.65V5.414Zm8.44 3.293a.47.47 0 0 0-.443.47v5.645a.47.47 0 0 0 .712.404l4.704-2.823a.47.47 0 0 0 0-.806L10.36 8.774a.47.47 0 0 0-.27-.067Zm.498 1.301L13.909 12l-3.32 1.992v-3.984ZM1.65 19.526h1.411v1.412h-1.41v-1.412Zm2.352 0h1.882v1.412H4.003v-1.412Zm2.823 0h1.881v1.412h-1.88v-1.412Zm2.822 0h1.882v1.412H9.648v-1.412Zm2.822 0h1.882v1.412H12.47v-1.412Zm2.823 0h1.881v1.412h-1.881v-1.412Zm2.822 0h1.882v1.412h-1.882v-1.412Zm2.823 0h1.41v1.412h-1.41v-1.412Z"
          />
        </Svg>
      );
    case "account-arrow-right":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 19c0-2.21-2.686-4-6-4s-6 1.79-6 4m6-7a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
          />
          <Path
            fill={color}
            fillRule="evenodd"
            d="M14.8 14c0 .22.179.4.4.4h4.634l-.917.917a.4.4 0 0 0-.033.528l.033.038a.4.4 0 0 0 .528.033l.038-.033 1.6-1.6.016-.018.022-.027.03-.044.02-.046.015-.042.011-.059L21.2 14l-.001-.03-.007-.05-.012-.045-.017-.044-.021-.04-.027-.038a.379.379 0 0 0-.032-.036l-1.6-1.6a.4.4 0 0 0-.6.528l.034.038.917.917H15.2a.4.4 0 0 0-.4.4Z"
            clipRule="evenodd"
          />
        </Svg>
      );
    case "send":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="m10.308 13.692 4.846-4.846M20.11 5.89l-4.09 13.294c-.367 1.192-.55 1.788-.867 1.985a.999.999 0 0 1-.912.076c-.345-.143-.624-.7-1.182-1.816l-2.59-5.182a2.104 2.104 0 0 0-.193-.342 1.002 1.002 0 0 0-.18-.181 2.036 2.036 0 0 0-.331-.186L4.572 10.94c-1.115-.558-1.673-.837-1.816-1.181a1 1 0 0 1 .076-.913c.197-.316.793-.5 1.985-.867l13.295-4.09c.937-.289 1.405-.433 1.722-.316a1 1 0 0 1 .594.594c.116.316-.028.784-.316 1.72v.002Z"
          />
        </Svg>
      );
    case "volume-off":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeWidth={1.5}
            d="M3.158 13.93a3.752 3.752 0 0 1 0-3.86 1.5 1.5 0 0 1 .993-.7l1.693-.339a.45.45 0 0 0 .258-.153L8.17 6.395c1.182-1.42 1.774-2.129 2.301-1.938C11 4.648 11 5.572 11 7.42v9.162c0 1.847 0 2.77-.528 2.962-.527.19-1.119-.519-2.301-1.938L6.1 15.122a.45.45 0 0 0-.257-.153L4.15 14.63a1.5 1.5 0 0 1-.993-.7Z"
          />
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeWidth={1.5}
            d="m15 15 6-6m0 6-6-6"
          />
        </Svg>
      );
    case "volume-high":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeWidth={1.5}
            d="M3.158 13.93a3.752 3.752 0 0 1 0-3.86 1.5 1.5 0 0 1 .993-.7l1.693-.339a.45.45 0 0 0 .258-.153L8.17 6.395c1.182-1.42 1.774-2.129 2.301-1.938C11 4.648 11 5.572 11 7.42v9.162c0 1.847 0 2.77-.528 2.962-.527.19-1.119-.519-2.301-1.938L6.1 15.122a.45.45 0 0 0-.257-.153L4.15 14.63a1.5 1.5 0 0 1-.993-.7Z"
          />
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeWidth={1.5}
            d="M15.536 8.464a5 5 0 0 1 .027 7.044m4.094-9.165a8 8 0 0 1 .044 11.27"
          />
        </Svg>
      );
    case "bell-check-outline":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13.457 17.309v.95a2.852 2.852 0 0 1-5.704 0v-.95m5.704 0H7.753m5.704 0h3.413c.364 0 .547 0 .694-.05a.95.95 0 0 0 .596-.597c.05-.147.05-.33.05-.697 0-.16 0-.24-.013-.317a.949.949 0 0 0-.165-.4c-.045-.063-.102-.12-.215-.234l-.37-.37a.637.637 0 0 1-.187-.45v-3.54a6.655 6.655 0 0 0-13.31 0v3.54a.637.637 0 0 1-.186.45l-.37.37c-.114.113-.17.17-.216.233a.95.95 0 0 0-.165.4C3 15.725 3 15.805 3 15.966c0 .366 0 .55.05.697a.951.951 0 0 0 .597.597c.147.05.33.05.693.05h3.413"
          />
          <Rect width={9} height={9} x={13} y={3} fill="#fff" rx={4.5} />
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="m15.25 7.5 1.591 1.59 3.182-3.181"
          />
        </Svg>
      );
    case "bell-alert-outline":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.282}
            d="M13.457 17.309v.95a2.852 2.852 0 0 1-5.704 0v-.95m5.704 0H7.753m5.704 0h3.413c.364 0 .547 0 .694-.05a.95.95 0 0 0 .596-.597c.05-.147.05-.33.05-.697 0-.16 0-.24-.013-.317a.949.949 0 0 0-.165-.4c-.045-.063-.102-.12-.215-.234l-.37-.37a.637.637 0 0 1-.187-.45v-3.54a6.655 6.655 0 0 0-13.31 0v3.54a.637.637 0 0 1-.186.45l-.37.37c-.114.113-.17.17-.216.233a.95.95 0 0 0-.165.4C3 15.725 3 15.805 3 15.966c0 .366 0 .55.05.697a.951.951 0 0 0 .597.597c.147.05.33.05.693.05h3.413"
          />
          <Path
            fill={color}
            d="M20.083 10.19a.584.584 0 0 1-.448-.175c-.108-.116-.174-.287-.2-.51l-.398-4.26c-.041-.373.03-.672.212-.896.183-.233.46-.349.834-.349.357 0 .627.116.81.349.183.224.253.523.212.896l-.399 4.26c-.025.223-.091.394-.2.51-.099.116-.24.174-.423.174Zm0 2.764c-.332 0-.597-.1-.797-.3-.19-.198-.286-.456-.286-.771 0-.307.096-.556.286-.747.2-.2.465-.3.797-.3.333 0 .59.1.773.3.19.19.286.44.286.747 0 .315-.096.573-.287.772-.182.2-.44.299-.772.299Z"
          />
        </Svg>
      );
    case "sparkles":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <G clipPath="url(#a)">
            <Path
              fill={color}
              d="M9.59 23.427a.45.45 0 0 1-.45-.45 8.213 8.213 0 0 0-8.492-8.492.45.45 0 0 1 0-.9 8.212 8.212 0 0 0 8.492-8.49.45.45 0 1 1 .9 0 8.212 8.212 0 0 0 8.491 8.49.45.45 0 1 1 0 .9 8.215 8.215 0 0 0-8.492 8.492.45.45 0 0 1-.45.45ZM3.7 14.036a8.813 8.813 0 0 1 5.89 5.888 8.813 8.813 0 0 1 5.888-5.888 8.813 8.813 0 0 1-5.888-5.89 8.814 8.814 0 0 1-5.89 5.89Zm14.97-3.2a.45.45 0 0 1-.45-.45 4.048 4.048 0 0 0-4.231-4.231.45.45 0 1 1 0-.9 4.05 4.05 0 0 0 4.232-4.232.45.45 0 1 1 .9 0 4.05 4.05 0 0 0 4.231 4.232.45.45 0 1 1 0 .9 4.048 4.048 0 0 0-4.232 4.23.45.45 0 0 1-.45.45Zm-2.467-5.131a4.78 4.78 0 0 1 2.467 2.467 4.78 4.78 0 0 1 2.468-2.467 4.783 4.783 0 0 1-2.468-2.468 4.783 4.783 0 0 1-2.467 2.468Z"
            />
          </G>
          <Defs>
            <ClipPath id="a">
              <Path fill="#fff" d="M0 0h24v24H0z" />
            </ClipPath>
          </Defs>
        </Svg>
      );

    case "compass":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0Z"
          />
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M10.5 10.5 16 8l-2.5 5.5L8 16l2.5-5.5Z"
          />
        </Svg>
      );
    case "information-outline":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 11v5m0 5a9 9 0 1 1 0-18 9 9 0 0 1 0 18Zm.05-13v.1h-.1V8h.1Z"
          />
        </Svg>
      );
    case "chevron-right":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="m9 5 7 7-7 7"
          />
        </Svg>
      );
    case "chevron-left":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="m15 19-7-7 7-7"
          />
        </Svg>
      );
    case "splash": {
      const width = size;
      const aspectRatio = 803 / 787;
      const height = width / aspectRatio;
      return (
        <Svg width={width} height={height} viewBox="0 0 803 787" fill="none">
          <Rect
            x={48}
            y={141.812}
            width={620.4}
            height={542.4}
            rx={72}
            stroke="white"
            strokeWidth={96}
          />
          <Path
            d="M255.6 3.81217C251.078 -1.03283 243.484 -1.29468 238.639 3.22733C233.794 7.74934 233.532 15.3428 238.054 20.1878L255.6 3.81217ZM356.4 111.812L255.6 3.81217L238.054 20.1878L338.854 128.188L356.4 111.812Z"
            fill="white"
          />
          <Path
            d="M461.254 3.81217C465.776 -1.03283 473.369 -1.29468 478.214 3.22733C483.059 7.74934 483.321 15.3428 478.799 20.1878L461.254 3.81217ZM360.454 111.812L461.254 3.81217L478.799 20.1878L377.999 128.188L360.454 111.812Z"
            fill="white"
          />
          <Path
            d="M262.98 383.992C255.9 383.992 249.6 382.252 244.08 378.772C238.68 375.172 235.08 370.432 233.28 364.552L235.26 358.612V369.772C235.26 374.212 234.12 377.632 231.84 380.032C229.56 382.432 226.26 383.632 221.94 383.632C217.62 383.632 214.26 382.432 211.86 380.032C209.58 377.632 208.44 374.212 208.44 369.772V267.352C208.44 262.792 209.58 259.372 211.86 257.092C214.26 254.692 217.68 253.492 222.12 253.492C226.44 253.492 229.74 254.692 232.02 257.092C234.42 259.372 235.62 262.792 235.62 267.352V310.012H233.46C235.38 304.612 239.04 300.232 244.44 296.872C249.84 293.512 256.02 291.832 262.98 291.832C270.78 291.832 277.56 293.692 283.32 297.412C289.08 301.132 293.58 306.412 296.82 313.252C300.06 320.092 301.68 328.252 301.68 337.732C301.68 347.212 300.06 355.432 296.82 362.392C293.58 369.352 289.02 374.692 283.14 378.412C277.38 382.132 270.66 383.992 262.98 383.992ZM254.88 363.652C258.72 363.652 262.08 362.692 264.96 360.772C267.96 358.852 270.24 356.032 271.8 352.312C273.48 348.472 274.32 343.612 274.32 337.732C274.32 328.972 272.52 322.552 268.92 318.472C265.32 314.272 260.64 312.172 254.88 312.172C251.04 312.172 247.62 313.072 244.62 314.872C241.74 316.672 239.46 319.492 237.78 323.332C236.1 327.052 235.26 331.852 235.26 337.732C235.26 346.492 237.06 353.032 240.66 357.352C244.26 361.552 249 363.652 254.88 363.652ZM360.783 383.992C351.303 383.992 343.023 382.132 335.943 378.412C328.983 374.692 323.583 369.352 319.743 362.392C315.903 355.432 313.983 347.212 313.983 337.732C313.983 330.652 315.063 324.292 317.223 318.652C319.383 313.012 322.503 308.212 326.583 304.252C330.783 300.172 335.763 297.112 341.523 295.072C347.283 292.912 353.703 291.832 360.783 291.832C370.263 291.832 378.483 293.692 385.443 297.412C392.523 301.132 397.983 306.412 401.823 313.252C405.783 320.092 407.763 328.252 407.763 337.732C407.763 344.932 406.623 351.352 404.343 356.992C402.183 362.632 399.063 367.492 394.983 371.572C390.903 375.652 385.923 378.772 380.043 380.932C374.283 382.972 367.863 383.992 360.783 383.992ZM360.783 363.652C364.743 363.652 368.163 362.692 371.043 360.772C373.923 358.852 376.203 356.032 377.883 352.312C379.563 348.472 380.403 343.612 380.403 337.732C380.403 328.972 378.603 322.552 375.003 318.472C371.403 314.272 366.663 312.172 360.783 312.172C356.943 312.172 353.523 313.072 350.523 314.872C347.643 316.672 345.363 319.492 343.683 323.332C342.123 327.052 341.343 331.852 341.343 337.732C341.343 346.492 343.143 353.032 346.743 357.352C350.343 361.552 355.023 363.652 360.783 363.652ZM437.979 383.632C433.539 383.632 430.119 382.432 427.719 380.032C425.439 377.632 424.299 374.212 424.299 369.772V267.352C424.299 262.792 425.439 259.372 427.719 257.092C430.119 254.692 433.539 253.492 437.979 253.492C442.299 253.492 445.599 254.692 447.879 257.092C450.279 259.372 451.479 262.792 451.479 267.352V330.172H451.839L477.939 301.192C480.579 298.192 482.979 295.972 485.139 294.532C487.419 292.972 490.419 292.192 494.139 292.192C497.859 292.192 500.679 293.152 502.599 295.072C504.639 296.992 505.659 299.332 505.659 302.092C505.779 304.732 504.639 307.432 502.239 310.192L474.699 340.972V330.712L505.299 365.992C507.579 368.752 508.539 371.512 508.179 374.272C507.939 377.032 506.739 379.312 504.579 381.112C502.419 382.792 499.659 383.632 496.299 383.632C492.219 383.632 488.919 382.852 486.399 381.292C483.999 379.732 481.539 377.332 479.019 374.092L451.839 343.852H451.479V369.772C451.479 379.012 446.979 383.632 437.979 383.632Z"
            fill="white"
          />
          <Path
            d="M264.179 562.792C257.099 562.792 250.799 561.052 245.279 557.572C239.879 553.972 236.279 549.232 234.479 543.352L236.459 537.412V548.572C236.459 553.012 235.319 556.432 233.039 558.832C230.759 561.232 227.459 562.432 223.139 562.432C218.819 562.432 215.459 561.232 213.059 558.832C210.779 556.432 209.639 553.012 209.639 548.572V446.152C209.639 441.592 210.779 438.172 213.059 435.892C215.459 433.492 218.879 432.292 223.319 432.292C227.639 432.292 230.939 433.492 233.219 435.892C235.619 438.172 236.819 441.592 236.819 446.152V488.812H234.659C236.579 483.412 240.239 479.032 245.639 475.672C251.039 472.312 257.219 470.632 264.179 470.632C271.979 470.632 278.759 472.492 284.519 476.212C290.279 479.932 294.779 485.212 298.019 492.052C301.259 498.892 302.879 507.052 302.879 516.532C302.879 526.012 301.259 534.232 298.019 541.192C294.779 548.152 290.219 553.492 284.339 557.212C278.579 560.932 271.859 562.792 264.179 562.792ZM256.079 542.452C259.919 542.452 263.279 541.492 266.159 539.572C269.159 537.652 271.439 534.832 272.999 531.112C274.679 527.272 275.519 522.412 275.519 516.532C275.519 507.772 273.719 501.352 270.119 497.272C266.519 493.072 261.839 490.972 256.079 490.972C252.239 490.972 248.819 491.872 245.819 493.672C242.939 495.472 240.659 498.292 238.979 502.132C237.299 505.852 236.459 510.652 236.459 516.532C236.459 525.292 238.259 531.832 241.859 536.152C245.459 540.352 250.199 542.452 256.079 542.452ZM361.982 562.792C352.503 562.792 344.223 560.932 337.143 557.212C330.183 553.492 324.783 548.152 320.942 541.192C317.103 534.232 315.183 526.012 315.183 516.532C315.183 509.452 316.263 503.092 318.423 497.452C320.583 491.812 323.703 487.012 327.783 483.052C331.983 478.972 336.963 475.912 342.723 473.872C348.483 471.712 354.903 470.632 361.982 470.632C371.463 470.632 379.683 472.492 386.643 476.212C393.723 479.932 399.183 485.212 403.023 492.052C406.983 498.892 408.963 507.052 408.963 516.532C408.963 523.732 407.823 530.152 405.543 535.792C403.383 541.432 400.263 546.292 396.183 550.372C392.103 554.452 387.123 557.572 381.243 559.732C375.483 561.772 369.063 562.792 361.982 562.792ZM361.982 542.452C365.943 542.452 369.363 541.492 372.243 539.572C375.123 537.652 377.403 534.832 379.083 531.112C380.763 527.272 381.603 522.412 381.603 516.532C381.603 507.772 379.803 501.352 376.203 497.272C372.603 493.072 367.863 490.972 361.982 490.972C358.143 490.972 354.723 491.872 351.723 493.672C348.843 495.472 346.563 498.292 344.883 502.132C343.323 505.852 342.543 510.652 342.543 516.532C342.543 525.292 344.343 531.832 347.943 536.152C351.543 540.352 356.223 542.452 361.982 542.452ZM439.179 562.432C434.739 562.432 431.319 561.232 428.919 558.832C426.639 556.432 425.499 553.012 425.499 548.572V446.152C425.499 441.592 426.639 438.172 428.919 435.892C431.319 433.492 434.739 432.292 439.179 432.292C443.499 432.292 446.799 433.492 449.079 435.892C451.479 438.172 452.679 441.592 452.679 446.152V508.972H453.039L479.139 479.992C481.779 476.992 484.179 474.772 486.339 473.332C488.619 471.772 491.619 470.992 495.339 470.992C499.059 470.992 501.879 471.952 503.799 473.872C505.839 475.792 506.859 478.132 506.859 480.892C506.979 483.532 505.839 486.232 503.439 488.992L475.899 519.772V509.512L506.499 544.792C508.779 547.552 509.739 550.312 509.379 553.072C509.139 555.832 507.939 558.112 505.779 559.912C503.619 561.592 500.859 562.432 497.499 562.432C493.419 562.432 490.119 561.652 487.599 560.092C485.199 558.532 482.739 556.132 480.219 552.892L453.039 522.652H452.679V548.572C452.679 557.812 448.179 562.432 439.179 562.432Z"
            fill="white"
          />
          <Rect
            x={541}
            y={521}
            width={261.888}
            height={265.728}
            rx={130.944}
            fill="white"
          />
          <Path
            d="M628.962 692.901C626.043 692.901 623.816 692.094 622.28 690.481C620.744 688.869 619.976 686.603 619.976 683.685V625.393H599.586C597.205 625.393 595.362 624.741 594.056 623.435C592.751 622.129 592.098 620.325 592.098 618.021C592.098 615.64 592.751 613.835 594.056 612.606C595.362 611.301 597.205 610.648 599.586 610.648H658.223C660.603 610.648 662.447 611.301 663.752 612.606C665.058 613.835 665.711 615.64 665.711 618.021C665.711 620.325 665.058 622.129 663.752 623.435C662.447 624.741 660.603 625.393 658.223 625.393H637.832V683.685C637.832 686.603 637.064 688.869 635.528 690.481C634.069 692.094 631.88 692.901 628.962 692.901ZM709.076 692.901C706.619 692.901 704.583 692.363 702.971 691.288C701.435 690.136 700.167 688.408 699.169 686.104L670.139 621.592C669.063 619.288 668.756 617.253 669.217 615.486C669.678 613.643 670.638 612.222 672.097 611.224C673.556 610.149 675.323 609.611 677.396 609.611C680.007 609.611 682.004 610.225 683.387 611.454C684.769 612.606 685.921 614.334 686.843 616.638L712.302 675.505H706.542L732.001 616.523C732.999 614.219 734.19 612.491 735.572 611.339C736.955 610.187 738.875 609.611 741.332 609.611C743.406 609.611 745.095 610.149 746.401 611.224C747.783 612.222 748.628 613.643 748.935 615.486C749.319 617.253 749.012 619.288 748.014 621.592L718.983 686.104C717.908 688.408 716.603 690.136 715.067 691.288C713.607 692.363 711.611 692.901 709.076 692.901Z"
            fill="#D43300"
          />
        </Svg>
      );
    }
    case "edit":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21 20C21.5523 20 22 20.4477 22 21C22 21.5523 21.5523 22 21 22H3C2.44772 22 2 21.5523 2 21C2 20.4477 2.44772 20 3 20H21ZM6.29289 13.2929L17.2929 2.29289C17.6534 1.93241 18.2206 1.90468 18.6129 2.2097L18.7071 2.29289L21.7071 5.29289C22.0676 5.65338 22.0953 6.22061 21.7903 6.6129L21.7071 6.70711L10.7071 17.7071C10.5508 17.8634 10.3481 17.9626 10.1314 17.9913L10 18H7C6.48716 18 6.06449 17.614 6.00673 17.1166L6 17V14C6 13.779 6.07316 13.5655 6.20608 13.392L6.29289 13.2929L17.2929 2.29289L6.29289 13.2929ZM18 4.41421L8 14.4142V16H9.58579L19.5858 6L18 4.41421Z"
            fill={color}
          />
        </Svg>
      );

    case "google":
      return (
        <Svg width={size} height={size} fill="none" viewBox="0 0 20 21">
          <Path
            d="M18.17 8.62h-.67v-.035H10v3.333h4.709a4.998 4.998 0 0 1-9.71-1.667 5 5 0 0 1 5-5c1.275 0 2.434.481 3.317 1.267l2.357-2.358A8.295 8.295 0 0 0 10 1.918a8.334 8.334 0 1 0 8.172 6.701Z"
            fill="#FFC107"
          />
          <Path
            d="M2.627 6.373 5.365 8.38a4.998 4.998 0 0 1 4.634-3.129c1.275 0 2.435.481 3.317 1.267l2.358-2.358a8.295 8.295 0 0 0-5.675-2.242 8.329 8.329 0 0 0-7.372 4.455Z"
            fill="#FF3D00"
          />
          <Path
            d="M10 18.586a8.294 8.294 0 0 0 5.588-2.164l-2.58-2.182a4.962 4.962 0 0 1-3.007 1.012 4.998 4.998 0 0 1-4.701-3.31l-2.718 2.093a8.327 8.327 0 0 0 7.419 4.55Z"
            fill="#4CAF50"
          />
          <Path
            d="M18.171 8.62H17.5v-.034H10v3.333h4.71a5.017 5.017 0 0 1-1.703 2.322v-.001l2.58 2.182c-.182.166 2.746-2.003 2.746-6.17 0-.558-.057-1.104-.162-1.631Z"
            fill="#1976D2"
          />
        </Svg>
      );

    case "arrow-back":
    case "radio-button-on":
    case "radio-button-off":
      return <MaterialIcons size={size} color={color} name={name} />;
    case "arrow-right":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="m13.71 4.387.083-.094a1 1 0 0 1 1.32-.083l.094.083 7 7 .041.044.056.068.071.11.054.114.035.105.03.148.006.118-.003.075-.017.126-.03.111-.044.111-.052.098-.074.104-.073.082-7 7a1 1 0 0 1-1.497-1.32l.083-.094L19.084 13H3.5a1 1 0 1 1 0-2h15.586l-5.293-5.293a1 1 0 0 1-.083-1.32l.083-.094-.083.094Z"
            fill={color}
          />
        </Svg>
      );
    case "arrow-left":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="m11.29 4.387-.083-.094a1 1 0 0 0-1.32-.083l-.094.083-7 7-.042.044-.055.068-.071.11-.054.114-.035.105-.03.148L2.5 12l.003.075.017.126.03.111.044.111.052.098.074.104.073.082 7 7a1 1 0 0 0 1.497-1.32l-.083-.094L5.916 13H21.5a1 1 0 1 0 0-2H5.914l5.293-5.293a1 1 0 0 0 .083-1.32l-.083-.094.083.094Z"
            fill={color}
          />
        </Svg>
      );
    case "photo-placeholder":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Rect x={0.5} y={0.5} width={size} height={size} rx={6} fill="#fff" />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M24.5 56.5v-8l24-24 8 8v24h-32Z"
            fill="#F6E5EE"
          />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M16.5 24.5a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm24 32-16-16-16 16h32Z"
            fill="#E9BFD3"
          />
        </Svg>
      );
    case "video-placeholder":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Rect y={0.5} width={size} height={size} rx={6} fill="#fff" />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M32 8.5c13.255 0 24 10.745 24 24s-10.745 24-24 24-24-10.745-24-24 10.745-24 24-24Zm0 4c-11.046 0-20 8.954-20 20s8.954 20 20 20 20-8.954 20-20-8.954-20-20-20Z"
            fill="#F6E5EE"
          />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="m29.234 24.805 9.729 5.858a2.147 2.147 0 0 1 0 3.674l-9.73 5.857a2.131 2.131 0 0 1-2.929-.735A2.148 2.148 0 0 1 26 38.358V26.642c0-1.183.956-2.142 2.135-2.142.387 0 .767.105 1.099.305Z"
            fill="#E9BFD3"
          />
        </Svg>
      );
    case "upload":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11 6a1 1 0 0 1 .993.883L12 7v2a3 3 0 0 1-2.824 2.995L9 12H3A3 3 0 0 1 .005 9.176L0 9V7a1 1 0 0 1 1.993-.117L2 7v2a1 1 0 0 0 .883.993L3 10h6a1 1 0 0 0 .993-.883L10 9V7a1 1 0 0 1 1-1ZM5.852.011 5.91.004 6 0l.075.003.126.017.111.03.111.044.098.052.104.074.082.073 2 2a1 1 0 0 1-1.414 1.414L7 3.415V7a1 1 0 0 1-2 0V3.414l-.293.293a1 1 0 0 1-1.414-1.414l2-2c.035-.036.073-.068.112-.097l.11-.071L5.63.07l.105-.035.118-.025Z"
            fill={color}
          />
        </Svg>
      );
    case "play":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 48 48"
          fill="none"
          {...props}
        >
          <Rect width={48} height={48} rx={16} fill={color} />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M18 17a1 1 0 0 1 1.504-.864l12 7a1 1 0 0 1 0 1.728l-12 7A1 1 0 0 1 18 31V17Zm2 1.741v10.518L29.015 24 20 18.741Z"
            fill="#FBF2F6"
          />
        </Svg>
      );
    case "play-alt":
      return (
        <Svg width={size} height={size} viewBox="0 0 48 48" {...props}>
          <Path
            fillRule="nonzero"
            clipRule="nonzero"
            d="M18 17a1 1 0 0 1 1.504-.864l12 7a1 1 0 0 1 0 1.728l-12 7A1 1 0 0 1 18 31V17Zm2 1.741v10.518L29.015 24 20 18.741Z"
            fill={color}
          />
        </Svg>
      );
    case "dislike":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M23.332.667a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H21.49l-4.229 8.455a4 4 0 0 1-3.311 2.203l-.266.008h-.352a3.334 3.334 0 0 1-3.327-3.114L9.998 24v-7.333h-5.33a4 4 0 0 1-3.962-4.553l.04-.232 1.6-8A4 4 0 0 1 6.03.674l.236-.007h17.065ZM21.998 14h1.334c.736 0 1.333-.597 1.333-1.333v-8c0-.737-.597-1.334-1.333-1.334h-1.334V14Zm-8.666 10.667h.352c.449 0 .863-.226 1.109-.593l.083-.144 4.315-8.63c.07-.139.114-.288.132-.442l.009-.154V3.334H6.267c-.582 0-1.09.376-1.267.92l-.04.151-1.6 8a1.333 1.333 0 0 0 1.046 1.57l.13.018.131.007h5.331a2.667 2.667 0 0 1 2.66 2.468l.007.199V24c0 .327.236.6.547.656l.12.01Z"
            fill={color}
          />
        </Svg>
      );
    case "like":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.167 27.333a4 4 0 0 1-4-4v-8a4 4 0 0 1 4-4h1.841l4.23-8.455a4 4 0 0 1 3.31-2.203l.267-.008h.352a3.333 3.333 0 0 1 3.326 3.114L17.5 4v7.333h5.331a4 4 0 0 1 3.962 4.553l-.04.232-1.6 8a4 4 0 0 1-3.685 3.208l-.237.007H4.167ZM5.5 14H4.167c-.737 0-1.334.597-1.334 1.333v8c0 .737.597 1.334 1.334 1.334H5.5V14Zm8.667-10.667h-.352c-.45 0-.864.226-1.11.593l-.083.144-4.315 8.63c-.069.139-.113.288-.131.442l-.01.154v11.37h13.065c.583 0 1.091-.376 1.268-.92l.04-.151 1.6-8a1.333 1.333 0 0 0-1.046-1.57l-.13-.018L22.83 14H17.5a2.667 2.667 0 0 1-2.66-2.468l-.007-.199V4c0-.327-.236-.6-.546-.656l-.12-.01Z"
            fill={color}
          />
        </Svg>
      );
    case "heart":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19.237 6.237a4.098 4.098 0 0 1 .135 5.654l-7.373 8.11-7.37-8.11a4.098 4.098 0 1 1 6.23-5.316L12 8l1.14-1.425a4.098 4.098 0 0 1 6.097-.338Z"
          />
        </Svg>
      );
    case "people":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7.5 14h1c1.636 0 3.088.786 4 2a4.992 4.992 0 0 1 3.743-1.993L16.5 14h1a5 5 0 0 1 5 5 1 1 0 0 1-1.993.117l-.012-.293a3 3 0 0 0-2.819-2.819L17.5 16h-1a3 3 0 0 0-3 3 1 1 0 0 1-.883.993L12.5 20a1 1 0 0 1-.993-.883l-.012-.293a3 3 0 0 0-2.819-2.819L8.5 16h-1a3 3 0 0 0-3 3 1 1 0 1 1-2 0 5 5 0 0 1 4.783-4.995L7.5 14Zm0-10a4 4 0 1 1 0 8 4 4 0 0 1 0-8Zm10 0a4 4 0 1 1 0 8 4 4 0 0 1 0-8Zm-10 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm10 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"
            fill={color}
          />
        </Svg>
      );
    case "user":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M20 21c0-2.761-3.582-5-8-5s-8 2.239-8 5m8-8a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z"
          />
        </Svg>
      );
    case "multiple-hearts":
      return (
        <Svg width={113} height={95} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M55.748 67.5 20.402 56.482c-5.162-1.608-9.28-5.099-11.596-9.83C6.49 41.92 6.26 36.526 8.157 31.46c3.536-9.441 13.592-15.056 23.341-13.513 4.764-8.645 15.367-13.14 24.992-10.14 5.164 1.611 9.283 5.103 11.598 9.834 2.315 4.731 2.545 10.126.647 15.189L55.748 67.5ZM94.56 43.636l-8.373-11.884c-1.223-1.736-1.671-3.808-1.26-5.835.41-2.027 1.628-3.762 3.43-4.887 3.36-2.095 7.834-1.439 10.501 1.373 3.551-1.553 7.929-.418 10.209 2.819 1.223 1.736 1.671 3.809 1.26 5.836-.41 2.027-1.629 3.762-3.43 4.885l-12.336 7.693Z"
            fill="#A80051"
          />
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M86.113 51.024c-1.975-1.14-4.36-1.565-6.689-1.004-2.23.54-4.072 1.9-5.187 3.832-1.114 1.93-1.37 4.205-.723 6.406l5.476 18.584 18.833-4.549c2.23-.54 4.073-1.9 5.187-3.83 1.114-1.929 1.371-4.207.724-6.408-1.354-4.594-6.246-7.418-10.901-6.294l-1.943.47-.564-1.917c-.678-2.297-2.239-4.15-4.213-5.29Zm-9.957 32.728L69.55 61.334c-.965-3.274-.578-6.669 1.09-9.559 1.669-2.89 4.415-4.922 7.734-5.725 6.186-1.495 12.65 1.782 15.257 7.434 6.198-.569 12.268 3.39 14.066 9.496.965 3.276.578 6.67-1.091 9.56-1.668 2.89-4.415 4.922-7.733 5.723l-22.717 5.489Z"
            fill="#E9BFD3"
          />
        </Svg>
      );
    case "calendar":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14 2a1 1 0 0 1 1 1v1a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3V3a1 1 0 0 1 2 0v1h6V3a1 1 0 0 1 1-1Zm2 8H4v5a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-5Zm-1-4H5a1 1 0 0 0-1 1v1h12V7a1 1 0 0 0-1-1Z"
            fill={color}
          />
        </Svg>
      );
    case "success":
      return (
        <Svg width={size} height={size} fill="none" {...props}>
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16ZM9 14a.997.997 0 0 1-.707-.292l-2-2a.999.999 0 1 1 1.414-1.414l1.138 1.138 3.323-4.986a1.001 1.001 0 0 1 1.664 1.11l-4 6A1 1 0 0 1 9 14Z"
            fill={color}
          />
        </Svg>
      );
    case "heart-full":
      return (
        <Svg
          viewBox="0 0 20 20"
          width={size}
          height={size}
          fill="none"
          {...props}
        >
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="m10 17-6.522-6.035C2.525 10.084 2 8.91 2 7.66s.525-2.423 1.478-3.305C5.254 2.71 8.042 2.564 10 3.909c1.958-1.345 4.746-1.198 6.522.446C17.475 5.237 18 6.41 18 7.66s-.525 2.424-1.478 3.305L10 17Z"
            fill={color}
          />
        </Svg>
      );

    case "settings":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="m20.35 8.923-.366-.204a2 2 0 0 1-.784-.724c-.017-.027-.033-.056-.065-.112a2.002 2.002 0 0 1-.3-1.157l.006-.425c.012-.68.018-1.022-.078-1.328a1.998 1.998 0 0 0-.417-.736c-.214-.24-.511-.412-1.106-.754l-.494-.285c-.592-.341-.889-.512-1.204-.577a1.999 1.999 0 0 0-.843.007c-.313.07-.606.246-1.191.596l-.003.002-.354.211c-.056.034-.085.05-.113.066-.278.155-.588.24-.907.25-.032.002-.065.002-.13.002l-.13-.001a1.997 1.997 0 0 1-.91-.252c-.028-.015-.055-.032-.111-.066l-.357-.214c-.589-.354-.884-.53-1.199-.601a1.998 1.998 0 0 0-.846-.006c-.316.066-.612.238-1.205.582l-.003.001-.488.283-.005.004c-.588.34-.883.512-1.095.751a2 2 0 0 0-.415.734c-.095.307-.09.649-.078 1.333l.007.424c0 .065.003.097.002.128a2.002 2.002 0 0 1-.301 1.027c-.033.056-.048.084-.065.11a2 2 0 0 1-.675.664l-.112.063-.361.2c-.602.333-.903.5-1.121.738a2 2 0 0 0-.43.73c-.1.307-.1.65-.099 1.338l.002.563c.001.683.003 1.024.104 1.329a2 2 0 0 0 .427.726c.218.236.516.402 1.113.734l.358.199c.061.034.092.05.121.068a2 2 0 0 1 .74.781l.067.12a2 2 0 0 1 .23 1.038l-.007.407c-.012.686-.017 1.03.079 1.337.085.272.227.523.417.736.214.24.512.411 1.106.754l.494.285c.593.341.889.512 1.204.577a2 2 0 0 0 .843-.007c.314-.07.607-.246 1.194-.598l.354-.212a1.997 1.997 0 0 1 1.02-.317h.26c.318.01.63.097.91.252l.092.055.376.226c.59.354.884.53 1.199.6a2 2 0 0 0 .846.008c.315-.066.613-.239 1.206-.583l.495-.287c.588-.342.883-.513 1.095-.752.19-.213.33-.463.415-.734.095-.305.09-.644.078-1.318l-.008-.44a2 2 0 0 1 .3-1.155l.065-.11a2 2 0 0 1 .675-.664l.11-.061.002-.001.361-.2c.602-.334.903-.5 1.122-.738.194-.21.34-.46.429-.73.1-.305.1-.647.098-1.327l-.002-.574c-.001-.683-.002-1.025-.103-1.33a2.002 2.002 0 0 0-.428-.725c-.217-.236-.515-.402-1.111-.733l-.002-.001Z"
          />
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 12a4 4 0 1 0 8 0 4 4 0 0 0-8 0Z"
          />
        </Svg>
      );
    case "bell":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 17v1a3 3 0 1 1-6 0v-1m6 0H9m6 0h3.59c.383 0 .575 0 .73-.052a1 1 0 0 0 .627-.628c.053-.156.053-.348.053-.734 0-.169 0-.253-.014-.334a.998.998 0 0 0-.173-.42c-.048-.067-.108-.127-.227-.246l-.39-.39a.67.67 0 0 1-.196-.474V10a7 7 0 1 0-14 0v3.722a.67.67 0 0 1-.196.474l-.39.39c-.12.12-.179.179-.226.245a1 1 0 0 0-.175.421c-.013.08-.013.165-.013.334 0 .386 0 .578.052.734a1 1 0 0 0 .629.628c.155.052.346.052.729.052H9"
          />
        </Svg>
      );
    case "bell-disabled":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 25" fill="none">
          <Path
            d="M9 17.7998V18.7998C9 20.4567 10.3431 21.7998 12 21.7998C13.6569 21.7998 15 20.4567 15 18.7998V17.7998M9 17.7998L15 17.7998M9 17.7998L5.00022 17.8C4.48738 17.8 4.06449 17.4139 4.00673 16.9166L4 16.7996V16.2139C4 15.9487 4.10544 15.6947 4.29297 15.5071L4.80371 14.9961C4.92939 14.8704 5 14.7001 5 14.5223V10.7997C5 8.95801 5.7112 7.28247 6.87393 6.03271M15 17.7998L18.9999 17.7998M5 3.7998L21 19.7998M18.9995 13.7988L19.0004 10.7998C19.0004 6.93382 15.8665 3.7998 12.0005 3.7998L11.7598 3.80386C10.9548 3.83106 10.1845 3.99385 9.4707 4.27062"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );

    case "eye-closed":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          {...props}
        >
          <Path
            d="M3.99989 4L19.9999 20M16.4999 16.7559C15.1473 17.4845 13.6185 17.9999 11.9999 17.9999C8.46924 17.9999 5.36624 15.5478 3.5868 13.7788C3.1171 13.3119 2.88229 13.0784 2.7328 12.6201C2.62619 12.2933 2.62616 11.7066 2.7328 11.3797C2.88233 10.9215 3.11763 10.6875 3.58827 10.2197C4.48515 9.32821 5.71801 8.26359 7.17219 7.42676M19.4999 14.6335C19.8329 14.3405 20.138 14.0523 20.4117 13.7803L20.4146 13.7772C20.8832 13.3114 21.1182 13.0779 21.2674 12.6206C21.374 12.2938 21.3738 11.7068 21.2672 11.38C21.1178 10.9219 20.8827 10.6877 20.4133 10.2211C18.6338 8.45208 15.5305 6 11.9999 6C11.6624 6 11.3288 6.02241 10.9999 6.06448M13.3228 13.5C12.9702 13.8112 12.5071 14 11.9999 14C10.8953 14 9.99989 13.1046 9.99989 12C9.99989 11.4605 10.2135 10.9711 10.5608 10.6113"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "eye":
      return (
        <Svg
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          {...props}
        >
          <Path
            d="M3.5868 13.7788C5.36623 15.5478 8.46953 17.9999 12.0002 17.9999C15.5308 17.9999 18.6335 15.5478 20.413 13.7788C20.8823 13.3123 21.1177 13.0782 21.2671 12.6201C21.3738 12.2933 21.3738 11.7067 21.2671 11.3799C21.1177 10.9218 20.8823 10.6877 20.413 10.2211C18.6335 8.45208 15.5308 6 12.0002 6C8.46953 6 5.36623 8.45208 3.5868 10.2211C3.11714 10.688 2.88229 10.9216 2.7328 11.3799C2.62618 11.7067 2.62618 12.2933 2.7328 12.6201C2.88229 13.0784 3.11714 13.3119 3.5868 13.7788Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            d="M10 12C10 13.1046 10.8954 14 12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "exclamation-mark-large":
      return (
        <Svg
          width={size}
          height={size}
          fill="none"
          viewBox="0 0 24 24"
          {...props}
        >
          <Path
            stroke={color}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 11v5m0 5a9 9 0 1 1 0-18 9 9 0 0 1 0 18Zm.05-13v.1h-.1V8h.1Z"
          />
        </Svg>
      );
    case "hamburger-menu":
      return (
        <Svg width={24} height={24} viewBox="0 0 24 24" fill="none" {...props}>
          <Path
            d="M5 17H19M5 12H19M5 7H19"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "warning":
      return (
        <Svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <Path
            d="M4.79167 4.79167L15.2083 15.2083M10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10C17.5 14.1421 14.1421 17.5 10 17.5Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "three-dot-menu":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            d="M11 18C11 18.5523 11.4477 19 12 19C12.5523 19 13 18.5523 13 18C13 17.4477 12.5523 17 12 17C11.4477 17 11 17.4477 11 18Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            d="M11 12C11 12.5523 11.4477 13 12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            d="M11 6C11 6.55228 11.4477 7 12 7C12.5523 7 13 6.55228 13 6C13 5.44772 12.5523 5 12 5C11.4477 5 11 5.44772 11 6Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "report":
      return (
        <Svg width={size} height={size} viewBox="0 0 20 20" fill="none">
          <Path
            d="M9.99996 7.04167V10.375M11.0384 2.88233L12.0558 3.74941C12.3122 3.96786 12.6305 4.09992 12.9662 4.12671L14.2987 4.23291C15.0822 4.29543 15.7045 4.91733 15.767 5.70077L15.8729 7.03353C15.8997 7.36925 16.0322 7.68799 16.2507 7.94433L17.1174 8.96152C17.6272 9.5597 17.6272 10.4396 17.1175 11.0378L16.2508 12.0551C16.0323 12.3115 15.9 12.6303 15.8732 12.9661L15.7666 14.2986C15.704 15.082 15.0827 15.7043 14.2993 15.7669L12.9663 15.8732C12.6305 15.9 12.312 16.0317 12.0556 16.2502L11.0384 17.1173C10.4402 17.627 9.55973 17.6271 8.96155 17.1174L7.9443 16.2502C7.68796 16.0318 7.36938 15.8998 7.03365 15.8731L5.70064 15.7668C4.9172 15.7043 4.29588 15.0822 4.23336 14.2988L4.12675 12.9661C4.09996 12.6304 3.96759 12.3118 3.74915 12.0555L2.88245 11.0378C2.37268 10.4397 2.37244 9.56002 2.88221 8.96184L3.74969 7.94418C3.96814 7.68784 4.09923 7.36926 4.12602 7.03353L4.23263 5.70093C4.29515 4.91749 4.91826 4.29535 5.70171 4.23283L7.03324 4.12663C7.36897 4.09984 7.6877 3.96788 7.94404 3.74943L8.96163 2.88233C9.55982 2.37256 10.4402 2.37256 11.0384 2.88233ZM10.0415 12.875V12.9583L9.95846 12.958V12.875H10.0415Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "exclamation-mark":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            d="M12 11V16M12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21ZM12.0498 8V8.1L11.9502 8.1002V8H12.0498Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "light-bulb":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            d="M9 21L15 21M12 3C8.68629 3 6 5.68629 6 9C6 10.2145 6.36084 11.3447 6.98117 12.2893C7.93507 13.7418 8.41161 14.4676 8.47352 14.5761C9.02428 15.541 8.92287 15.2007 8.99219 16.3096C8.99998 16.4342 9 16.6229 9 17C9 17.5523 9.44772 18 10 18L14 18C14.5523 18 15 17.5523 15 17C15 16.6229 15 16.4342 15.0078 16.3096C15.0771 15.2007 14.9751 15.541 15.5259 14.5761C15.5878 14.4676 16.0651 13.7418 17.019 12.2893C17.6394 11.3447 18.0002 10.2145 18.0002 9C18.0002 5.68629 15.3137 3 12 3Z"
            stroke={color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "location-large":
      return (
        <Svg width={82} height={105} viewBox="0 0 82 105" fill="none">
          <Path
            d="M3.05179 40.8465L3.05179 40.845C3.04413 25.5607 12.2482 11.778 26.369 5.92864L25.201 3.10919L26.369 5.92864C40.4898 0.0791985 56.7441 3.31604 67.5466 14.1286L67.5525 14.1345C74.6528 21.203 78.6298 30.8181 78.598 40.8369V40.8465C78.598 48.0741 76.054 55.676 72.2391 62.9631C68.4374 70.2251 63.4783 76.9763 58.8974 82.4476C53.3497 89.0687 47.3082 95.2579 40.8249 100.962C34.3416 95.2579 28.3 89.0687 22.7524 82.4476C18.1715 76.9763 13.2124 70.2251 9.41065 62.9631C5.59578 55.676 3.05179 48.0741 3.05179 40.8465ZM40.8249 20.2984C33.4837 20.2984 26.7002 24.2149 23.0297 30.5725C19.3591 36.9301 19.3591 44.7631 23.0297 51.1207C26.7002 57.4783 33.4837 61.3948 40.8249 61.3948C52.1737 61.3948 61.3731 52.1947 61.3731 40.8465C61.3731 29.4981 52.1736 20.2984 40.8249 20.2984Z"
            stroke={color}
            strokeWidth={6.10357}
          />
        </Svg>
      );
    case "bell-large":
      return (
        <Svg width={102} height={105} viewBox="0 0 102 105" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M78.8549 1.53085C80.1362 -0.164648 82.5532 -0.50338 84.2535 0.774269C91.8743 6.50064 97.8779 14.0974 101.68 22.8247C102.528 24.7719 101.633 27.0361 99.68 27.882C97.7272 28.7279 95.4566 27.835 94.6083 25.8878C91.3369 18.3783 86.171 11.8416 79.6136 6.91421C77.9133 5.63656 77.5736 3.22635 78.8549 1.53085ZM23.1451 1.53085C24.4264 3.22635 24.0867 5.63656 22.3864 6.91421C15.829 11.8416 10.6631 18.3783 7.39167 25.8878C6.54341 27.835 4.27274 28.7279 2.32 27.882C0.367261 27.0361 -0.528094 24.7719 0.320171 22.8247C4.12206 14.0974 10.1257 6.50064 17.7465 0.774269C19.4468 -0.50338 21.8638 -0.164648 23.1451 1.53085ZM50.9987 12.7435C33.2568 12.7436 18.8741 27.0855 18.8741 44.777V63.8558C18.8741 65.7886 18.1027 67.6392 16.7354 69.0027L14.7334 70.999C14.4119 71.3196 14.2315 71.5004 14.0994 71.6409C13.9847 71.7629 13.9661 71.7923 13.974 71.7814C13.8603 71.9394 13.7837 72.1228 13.7512 72.3198C13.7496 72.3328 13.7446 72.3832 13.7408 72.5041C13.7347 72.6973 13.7342 72.9517 13.7342 73.4071C13.7342 74.4385 13.7356 75.0527 13.7617 75.5124C13.7781 75.7996 13.8001 75.9226 13.8065 75.9537C13.9353 76.3199 14.2256 76.61 14.5952 76.7393C14.6266 76.7458 14.7484 76.7673 15.0308 76.7832C15.4875 76.8091 16.0976 76.8105 17.1243 76.8105H84.8734C85.8999 76.8105 86.5111 76.8091 86.969 76.7832C87.2525 76.7672 87.375 76.7456 87.4064 76.7392C87.7697 76.6119 88.0587 76.3259 88.1893 75.9539C88.1958 75.9229 88.2178 75.7999 88.2341 75.5125C88.2602 75.0526 88.2617 74.4384 88.2617 73.407C88.2617 72.9538 88.261 72.6993 88.2547 72.5075C88.2492 72.3391 88.2414 72.3064 88.2437 72.3202C88.2099 72.1149 88.1325 71.9329 88.0239 71.7817C88.0158 71.7715 87.9831 71.7317 87.8994 71.6428C87.7659 71.5009 87.5851 71.32 87.2633 70.9991L85.2611 69.0026C83.8923 67.6376 83.1232 65.7862 83.1232 63.8557V44.777C83.1232 27.0854 68.7406 12.7435 50.9987 12.7435ZM11.1643 44.777C11.1643 22.8395 28.9988 5.05556 50.9986 5.05551C72.9986 5.05545 90.8331 22.8394 90.8331 44.777V63.6862L92.7151 65.563C92.755 65.6028 92.7952 65.6428 92.8357 65.6831C93.3246 66.1694 93.8519 66.6939 94.286 67.2968C95.1005 68.428 95.6293 69.7228 95.8518 71.0752C95.9731 71.8117 95.9723 72.5497 95.9716 73.2506C95.9716 73.303 95.9715 73.3551 95.9715 73.407C95.9715 73.5423 95.9719 73.6784 95.9722 73.815C95.976 75.3602 95.98 76.9769 95.4997 78.3968C94.6038 81.0455 92.5218 83.1323 89.8555 84.0292C88.4346 84.5071 86.8167 84.5031 85.2793 84.4992C85.1433 84.4989 85.0079 84.4986 84.8733 84.4986H70.2734V85.7799C70.2734 96.3949 61.6438 105 50.9987 105C40.3535 105 31.7239 96.3949 31.7239 85.7799V84.4986H17.1243C16.9897 84.4986 16.8544 84.4989 16.7184 84.4992C15.1809 84.503 13.5665 84.507 12.1457 84.0292C9.48573 83.1345 7.39423 81.0516 6.49613 78.3966C6.01584 76.9767 6.01983 75.3603 6.02365 73.8152C6.02399 73.6785 6.02433 73.5424 6.02433 73.4071C6.02433 73.3507 6.02426 73.2939 6.02419 73.2368C6.02335 72.5457 6.02245 71.808 6.14306 71.0752C6.36702 69.7144 6.90203 68.4212 7.71168 67.2969C8.14791 66.6911 8.67278 66.1687 9.169 65.6749C9.20674 65.6374 9.24431 65.6 9.28166 65.5627L11.1643 63.6855V44.777ZM39.4338 84.4986V85.7799C39.4338 92.1489 44.6116 97.312 50.9987 97.312C57.3857 97.312 62.5635 92.1489 62.5635 85.7799V84.4986H39.4338Z"
            fill={color}
          />
        </Svg>
      );
    case "camera-large": {
      const aspectRatio = 125 / 105;
      const width = size;
      const height = size / aspectRatio;
      return (
        <Svg width={width} height={height} viewBox="0 0 125 105" fill="none">
          <Path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M53.0506 1.86978e-05C53.1025 2.81804e-05 53.1551 3.80422e-05 53.2084 3.80422e-05L71.04 1.86978e-05C71.9188 -0.000144402 72.6039 -0.000271468 73.2505 0.0473985C79.8269 0.532216 85.5734 4.6713 88.1137 10.7587C88.3674 11.3666 88.5861 12.0228 88.8734 12.8849L88.9081 12.9892C88.929 13.0519 88.9501 13.1149 88.9711 13.1779C89.1996 13.8614 89.4311 14.5539 89.5633 15.1735C89.8472 16.5037 89.8811 17.8251 89.6984 19.0909L99.1497 19.0909C102.549 19.0909 105.385 19.0908 107.701 19.2802C110.118 19.4778 112.386 19.905 114.532 20.9983C117.82 22.6735 120.504 25.3467 122.185 28.6447C123.278 30.7897 123.704 33.0583 123.902 35.473C124.091 37.7884 124.091 40.6217 124.091 44.0141V80.0768C124.091 83.4691 124.091 86.3013 123.902 88.6156C123.704 91.0298 123.277 93.2967 122.185 95.4411C120.507 98.7339 117.825 101.416 114.532 103.094C112.388 104.187 110.121 104.613 107.706 104.811C105.392 105 102.56 105 99.168 105H24.9231C21.5308 105 18.6975 105 16.3821 104.811C13.9673 104.614 11.6987 104.187 9.55375 103.094C6.25576 101.414 3.58254 98.7288 1.90739 95.4411C0.814109 93.2954 0.38686 91.0275 0.189273 88.6105C-0.000121851 86.2938 -6.53366e-05 83.4577 2.55851e-06 80.0588V44.035C-6.53366e-05 40.6357 -0.000121472 37.7979 0.189244 35.4794C0.386718 33.0617 0.813695 30.7912 1.90739 28.6447C3.58517 25.3519 6.26092 22.6761 9.55375 20.9983C11.7003 19.9046 13.9708 19.4777 16.3885 19.2802C18.7069 19.0908 21.5448 19.0909 24.944 19.0909L34.3924 19.0909C34.2094 17.8251 34.2432 16.5036 34.5271 15.1729C34.6583 14.5582 34.887 13.8741 35.109 13.2101C35.1296 13.1485 35.1501 13.0872 35.1705 13.026L35.1778 13.004L35.1828 12.9898C35.1996 12.9392 35.2162 12.8893 35.2326 12.8401C35.5104 12.0062 35.7266 11.3572 35.9764 10.7587C38.5175 4.66936 44.2697 0.532061 50.844 0.0473988C51.4907 -0.000269192 52.1717 -0.000143644 53.0506 1.86978e-05ZM46.0682 19.0909C46.0676 19.0909 46.0689 19.0909 46.0682 19.0909H78.0214C78.0207 19.0909 78.022 19.0909 78.0214 19.0909C78.3638 19.0908 78.5775 19.0897 78.732 19.0861C78.8104 19.0842 78.8522 19.0821 78.8678 19.0812C79.7841 18.9685 80.4183 18.0926 80.2307 17.1783C80.2268 17.1635 80.2099 17.1014 80.1665 16.9629C80.0994 16.749 80.0051 16.4654 79.8525 16.0077C79.5039 14.9619 79.4016 14.6675 79.3046 14.4349C78.1504 11.6692 75.5394 9.78749 72.5487 9.56702C72.2992 9.54862 71.9921 9.54549 70.8819 9.54549H53.2084C52.0987 9.54549 51.7955 9.54861 51.5458 9.56702C48.553 9.78765 45.9389 11.6711 44.7855 14.4349C44.6892 14.6658 44.5894 14.9552 44.2384 16.0083L44.231 16.0303L44.2261 16.0446C44.0776 16.4901 43.986 16.7653 43.9209 16.9731C43.88 17.1037 43.8636 17.1636 43.8595 17.1788C43.6735 18.089 44.307 18.9684 45.2273 19.0813C45.2426 19.0822 45.2838 19.0843 45.3611 19.0861C45.5141 19.0897 45.7263 19.0908 46.0682 19.0909ZM25.1376 28.6364C21.4949 28.6364 19.0492 28.6401 17.1656 28.7939C15.3374 28.9433 14.4633 29.2099 13.8873 29.5034C12.3906 30.266 11.1751 31.4815 10.4124 32.9782C10.119 33.5543 9.85234 34.4283 9.70301 36.2565C9.54917 38.1401 9.54546 40.5859 9.54546 44.2285V79.8649C9.54546 83.5077 9.54917 85.9513 9.70298 87.8328C9.8522 89.6579 10.1185 90.5307 10.4124 91.1076C11.1777 92.6095 12.3957 93.8289 13.8873 94.5889C14.4622 94.8818 15.3346 95.148 17.1595 95.2972C19.0399 95.4508 21.481 95.4546 25.1167 95.4546H98.9742C102.61 95.4546 105.05 95.4508 106.929 95.2972C108.751 95.1481 109.623 94.8821 110.198 94.5889C111.695 93.8263 112.917 92.6043 113.68 91.1076C113.973 90.5321 114.239 89.6605 114.388 87.8377C114.542 85.9588 114.545 83.5191 114.545 79.8833V44.2076C114.545 40.5719 114.542 38.1308 114.388 36.2505C114.239 34.4256 113.973 33.5531 113.68 32.9782C112.92 31.4866 111.7 30.2686 110.198 29.5034C109.622 29.2095 108.749 28.9431 106.924 28.7939C105.042 28.6401 102.599 28.6364 98.9558 28.6364H25.1376ZM62.0454 47.7273C54.1377 47.7273 47.7273 54.1378 47.7273 62.0455C47.7273 69.9532 54.1377 76.3636 62.0454 76.3636C69.9532 76.3636 76.3636 69.9532 76.3636 62.0455C76.3636 54.1378 69.9532 47.7273 62.0454 47.7273ZM38.1818 62.0455C38.1818 48.866 48.8659 38.1818 62.0454 38.1818C75.225 38.1818 85.9091 48.866 85.9091 62.0455C85.9091 75.225 75.225 85.9091 62.0454 85.9091C48.8659 85.9091 38.1818 75.225 38.1818 62.0455Z"
            fill={color}
          />
        </Svg>
      );
    }
    case "location-pin":
      return (
        <MaterialIcons
          size={size}
          color={color}
          name={name}
          {...(props as any)}
        />
      );

    case "logout":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <Path
            d="M12 15L15 12M15 12L12 9M15 12H4M9 7.24859V7.2002C9 6.08009 9 5.51962 9.21799 5.0918C9.40973 4.71547 9.71547 4.40973 10.0918 4.21799C10.5196 4 11.0801 4 12.2002 4H16.8002C17.9203 4 18.4796 4 18.9074 4.21799C19.2837 4.40973 19.5905 4.71547 19.7822 5.0918C20 5.5192 20 6.07899 20 7.19691V16.8036C20 17.9215 20 18.4805 19.7822 18.9079C19.5905 19.2842 19.2837 19.5905 18.9074 19.7822C18.48 20 17.921 20 16.8031 20H12.1969C11.079 20 10.5192 20 10.0918 19.7822C9.71547 19.5905 9.40973 19.2839 9.21799 18.9076C9 18.4798 9 17.9201 9 16.8V16.75"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Svg>
      );
    case "sliders":
      return (
        <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
          <G id="Interface / Slider_01">
            <Path
              id="Vector"
              d="M14 15H21M3 15H5M5 15C5 16.3807 6.11929 17.5 7.5 17.5C8.88071 17.5 10 16.3807 10 15C10 13.6193 8.88071 12.5 7.5 12.5C6.11929 12.5 5 13.6193 5 15ZM20 9H21M3 9H10M16.5 11.5C15.1193 11.5 14 10.3807 14 9C14 7.61929 15.1193 6.5 16.5 6.5C17.8807 6.5 19 7.61929 19 9C19 10.3807 17.8807 11.5 16.5 11.5Z"
              stroke={color}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </G>
        </Svg>
      );

    default:
      return (
        <MaterialCommunityIcons
          size={size}
          color={color}
          name={name}
          {...(props as any)}
        />
      );
  }
}
